import React, { useEffect, useMemo, useCallback, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getUserFirstName, selectComplianceEnabled, selectPersona, selectHasAnyTaskManagementBp, selectFeatureDefaultOwner, selectFeatureVisibility } from 'features/auth/ducks/selectors'
import PersonaSwitcher from 'features/dashboard/PersonaSwitcher'
import PersonaPrompt from 'features/dashboard/PersonaPrompt'
import FeatureSwitchBar from 'components/FeatureSwitchBar'
import { hiddenWidgetKeysFor } from 'constants/featureGroups'
import TeamMemberView from 'features/dashboard/views/TeamMemberView'
import PageHeader from 'components/atoms/PageHeader'
import styled, { keyframes } from 'styled-components'
import { isNotNilOrEmpty } from 'utils/ramda'
import BpBox from 'features/dashboard/components/BpBox'
import { getBuildingTypes, getUserBps } from 'features/bpList/ducks/selectors'
import { fetchUserBpsRoutine } from 'features/bpList/ducks/actions'
import { isEmpty, propOr, values } from 'ramda'
import TemplateBox from 'features/dashboard/components/TemplateBox'
import DashboardEmptyState from 'features/dashboard/components/DashboardEmptyState'
import { getDashboardStats, getDashboardLoading, getLastViewedBps } from 'features/dashboard/ducks/selectors'
import DashboardSkeleton from 'features/dashboard/sections/DashboardSkeleton'
import {
  fetchLastViewedBpsRoutine,
  setObligationOverrideRoutine,
} from 'features/dashboard/ducks/actions'
import { fetchUserUnitsRoutine } from 'ducks/units/actions'
import { useDashboardStats } from 'features/dashboard/useDashboardStats'
import { REMEDIAL_MIX_LEAD, taskMixDrill } from 'features/dashboard/progressMixDrills'
import { selectUnitsFetched, selectUnitsLoading, selectUserUnits } from 'ducks/units/selectors'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PRIVATE_PATHS } from 'utils/paths'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import EngineeringRoundedIcon from '@mui/icons-material/EngineeringRounded'
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded'
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded'
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded'
import { Tooltip } from '@mui/material'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import { vettingMeta } from 'features/contractors/contractorMeta'
import moment from 'moment'
import bpLogo from 'assets/images/bp-logo-orange.png'
import { isOnline as checkIsOnline } from 'ducks/offline/actions'
import GuruInsightModal from 'features/bpDetails/Components/files/GuruInsightModal'
import { setObligationOverride, clearObligationOverride, getComplianceAttention } from 'services/BpService'
import { listMyEstates } from 'services/estateService'
import NextStepsPanel from 'features/dashboard/sections/NextStepsPanel'
import { toast } from 'react-toastify'

import KpiTile from 'features/dashboard/charts/KpiTile'
import SegmentedDonut from 'features/dashboard/charts/SegmentedDonut'
import StackedDomainList from 'features/dashboard/charts/StackedDomainList'
import ComplianceMatrix from 'features/dashboard/charts/ComplianceMatrix'
import TrendLine from 'features/dashboard/charts/TrendLine'
import ProgressMixCard from 'features/dashboard/sections/ProgressMixCard'
import { taskBucketsWithWindow } from 'features/taskManagement/taskProgressBuckets'
import { REMEDIAL_PROGRESS_BUCKETS } from 'features/actions/workspace/remedialProgressBuckets'
import StatusPill from 'features/dashboard/charts/StatusPill'
import { scoreColour, scoreBandFor, formatPercent, scoreBand, RAMP, severityColour, criticalityLevel, weightedScore, RAG } from 'features/dashboard/charts/scoreColour'
import { remedialsViewUrl, resolvedDates } from 'features/actions/workspace/remedialsQuery'
import TaskDomainDrawer from 'features/dashboard/sections/TaskDomainDrawer'
import MissingObligations from 'features/dashboard/sections/MissingObligations'
import ContractorPerformanceWidget from 'features/dashboard/sections/ContractorPerformanceWidget'
import ViewTracker      from 'features/dashboard/sections/ViewTracker'
import CustomisableDashboard from 'features/dashboard/customise/CustomisableDashboard'
import PortfolioView    from 'features/dashboard/views/PortfolioView'
import BpView           from 'features/dashboard/views/BpView'
import UnitView         from 'features/dashboard/views/UnitView'
import UnitComplianceView from 'features/dashboard/views/UnitComplianceView'
import JumpToPicker     from 'features/dashboard/sections/JumpToPicker'
import DashboardTabs    from 'features/dashboard/sections/DashboardTabs'
import ComplianceSetup from 'features/dashboard/sections/ComplianceSetup'
import ComplianceSetupBanner from 'features/dashboard/sections/ComplianceSetupBanner'
import AvatarPromptBanner from 'features/dashboard/sections/AvatarPromptBanner'
import BulkActionWizard from 'features/dashboard/sections/BulkActionWizard'
import ShortcutHelp     from 'features/dashboard/sections/ShortcutHelp'
import OnboardingWizard, { shouldShowOnboarding } from 'features/dashboard/sections/OnboardingWizard'
import { isWorkflowRunning } from 'features/tours/workflowRunner'
import OpsInsightSection from 'features/dashboard/sections/OpsInsightSection'
import KpiReportDialog from 'features/dashboard/sections/KpiReportDialog'
import { resolvePeriod } from 'components/ReportPeriodPicker'
import DomainActivity from 'features/dashboard/sections/DomainActivity'
import TaskCompletionCard from 'features/dashboard/sections/TaskCompletionCard'
import TasksByPriorityCard from 'features/dashboard/sections/TasksByPriorityCard'
import RagGauge from 'features/dashboard/sections/RagGauge'
import RagBreakdown from 'features/dashboard/sections/RagBreakdown'
import ScoreList from 'features/dashboard/sections/ScoreList'
import TaskRowList from 'features/dashboard/sections/TaskRowList'
import TrendBars from 'features/dashboard/sections/TrendBars'
import StatCard from 'features/dashboard/sections/StatCard'
import PeriodDelta from 'features/dashboard/sections/PeriodDelta'
import PortfolioNavigator from 'features/dashboard/sections/PortfolioNavigator'
import ComplianceHeadline from 'features/dashboard/sections/ComplianceHeadline'
import ComplianceInactiveNotice from 'features/dashboard/sections/ComplianceInactiveNotice'
import PassportsBanner from 'features/dashboard/sections/PassportsBanner'
import { notifyComplianceChanged, useComplianceChanged, useRefetchOnFocus } from 'utils/complianceEvents'
import useKeyboardShortcuts from 'hooks/useKeyboardShortcuts'

import SectionTitle from 'features/dashboard/components/SectionTitle'
import { ink, surface, line, radius, shadow, font, bp, transition, lowerLabel } from 'features/dashboard/tokens'

const COMPLIANCE_WIDGET_KEYS = ['compliance_matrix', 'compliance_trend', 'missing_obligations', 'buildings_at_risk']

const TASK_WIDGET_KEYS = ['tasks_overview', 'actions_overview', 'incidents_overview', 'people_overview', 'ops_insights', 'domain_activity', 'task_completion', 'tasks_by_priority', 'ontime_gauge', 'remedials_severity', 'incidents_severity', 'awaiting_approval', 'top_performers', 'overdue_ageing', 'unassigned_tasks', 'ontime_trend', 'period_delta', 'due_this_week', 'most_overdue']
const COMPLIANCE_ONLY_WIDGET_KEYS = ['compliance_gauge', 'obligations_status', 'buildings_rag']

const SHARED_LAYOUT_KEY = 'portfolio'

const HeaderTooltip = ({ title, body, shortcut, children }) => (
  <Tooltip
    arrow
    placement='bottom'
    enterDelay={150}
    componentsProps={{
      tooltip: { sx: { bgcolor: '#0C1229', maxWidth: 260, p: 0, borderRadius: '10px', boxShadow: '0 12px 32px rgba(12,18,41,0.28)' } },
      arrow: { sx: { color: '#0C1229' } },
    }}
    title={
      <TipInner>
        <TipTitle>
          {title}
          {shortcut && <TipKey>{shortcut}</TipKey>}
        </TipTitle>
        <TipBody>{body}</TipBody>
      </TipInner>
    }
  >
    {children}
  </Tooltip>
)

const Dashboard = () => {
  const currentUserName = useSelector(getUserFirstName)
  const complianceEnabled = useSelector(selectComplianceEnabled)
  const hasAnyTaskManagementBp = useSelector(selectHasAnyTaskManagementBp)
  const featureVisibility = useSelector(selectFeatureVisibility)
  const featureOwner = useSelector(selectFeatureDefaultOwner)
  const persona = useSelector(selectPersona)
  const [widgetVisible, setWidgetVisible] = useState({})
  const WIDGET_PAGE = 8
  const visibleFor = key => widgetVisible[key] || WIDGET_PAGE
  const revealMore = key => setWidgetVisible(prev => ({ ...prev, [key]: (prev[key] || WIDGET_PAGE) + WIDGET_PAGE }))
  const dashboardStats = useSelector(getDashboardStats)
  const dashboardLoading = useSelector(getDashboardLoading)
  const bps = useSelector(getUserBps)
  const lastViewed = useSelector(getLastViewedBps)
  const buildingTypes = useSelector(getBuildingTypes)
  const units = useSelector(selectUserUnits)
  const unitsLoading = useSelector(selectUnitsLoading)
  const unitsFetched = useSelector(selectUnitsFetched)
  const bpsLoading = useSelector(state => state.bp.loading)
  const bpsFetched = useSelector(state => state.bp.fetched)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const altitude   = searchParams.get('altitude') || 'portfolio'
  const entityId   = searchParams.get('entityId') || null
  const assigneeId = searchParams.get('assignee') || null
  const period     = ['month', 'quarter', 'year'].includes(searchParams.get('period')) ? searchParams.get('period') : null
  const taskOrigin = ['corrective', 'obligation', 'standard'].includes(searchParams.get('origin')) ? searchParams.get('origin') : null
  const taskRange = useMemo(() => {
    const from = searchParams.get('from')
    const to = searchParams.get('to')
    if (from || to) return { preset: 'custom', from: from || to, to: to || from }
    if (searchParams.get('range')) return { preset: searchParams.get('range') }
    if (period) return { preset: `this_${period}` }
    return { preset: 'last_30_days' }
  }, [searchParams, period])
  const resolvedRange = useMemo(() => resolvePeriod(taskRange), [taskRange])
  const rangeText = resolvedRange.from
    ? `${moment(resolvedRange.from).format('D MMM YYYY')} to ${moment(resolvedRange.to).format('D MMM YYYY')}`
    : 'all time'
  const [kpiOpen, setKpiOpen] = useState(false)
  const filterQ    = searchParams.get('q') || ''
  const filterStat = searchParams.get('s') || null
  const filterSort = searchParams.get('o') || 'risk'
  const view       = searchParams.get('view') || 'action'

  const setView = next => {
    const params = new URLSearchParams(searchParams)
    if (next && next !== 'action') params.set('view', next)
    else                            params.delete('view')
    setSearchParams(params, { replace: true })
  }
  const dataFetchedRef = useRef(false)
  const [insightFile, setInsightFile] = useState(null)
  const [showInsightModal, setShowInsightModal] = useState(false)
  const [drawerDomain, setDrawerDomain] = useState(null)
  const [wizardOpen, setWizardOpen] = useState(false)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false)
  const [onboardingOpen, setOnboardingOpen] = useState(() => shouldShowOnboarding() && !isWorkflowRunning())
  const [attention, setAttention] = useState(null)
  const [myEstates, setMyEstates] = useState([])
  const setupPromptedRef = useRef(false)
  const [jumpOpen, setJumpOpen] = useState(false)
  const [jumpFilterType, setJumpFilterType] = useState(null)

  const scopeKey = `${altitude}|${entityId || ''}|${searchParams.get('domain') || ''}`
  const [renderedScope, setRenderedScope] = useState(scopeKey)

  useKeyboardShortcuts([
    { keys: '?', handler: () => setShortcutHelpOpen(true) },
    { keys: '/', handler: () => {
        const el = document.querySelector('input[placeholder^="Search Building Passports"]')
        if (el) el.focus()
      } },
    { keys: 'g d', handler: () => navigate('/dashboard') },
    { keys: '1',   handler: () => setView('action') },
    { keys: '2',   handler: () => setView('buildings') },
    { keys: '3',   handler: () => setView('trends') },
    { keys: 'w',   handler: () => setWizardOpen(true) },
    { keys: 'b',   handler: () => setBulkOpen(true) },
    { keys: 'j',   handler: () => { setJumpFilterType(null); setJumpOpen(true) } },
    { keys: 'esc', handler: () => { setWizardOpen(false); setBulkOpen(false); setShortcutHelpOpen(false); setJumpOpen(false) } },
  ], [navigate])

  const isLoading = (unitsLoading && !unitsFetched) || (bpsLoading && !bpsFetched)
  const isUnitOnly = isNotNilOrEmpty(units) && isEmpty(bps)

  useEffect(() => {
    if (!dataFetchedRef.current) {
      if (checkIsOnline()) {
        dispatch(fetchLastViewedBpsRoutine())
        dispatch(fetchUserBpsRoutine({ page: 1, sort: '-created_at' }))
        dispatch(fetchUserUnitsRoutine.trigger({ page: 1, sort: '-created_at' }))
      }
      dataFetchedRef.current = true
    }
  }, [dispatch])

  useDashboardStats({ altitude, entityId, assigneeId, origin: taskOrigin, from: resolvedRange.from, to: resolvedRange.to })

  const loadAttention = useCallback(() => {
    if (!checkIsOnline()) return
    if (!complianceEnabled || !hasAnyTaskManagementBp) return
    getComplianceAttention()
      .then(res => setAttention(res.data?.data || null))
      .catch(() => setAttention(null))
  }, [complianceEnabled, hasAnyTaskManagementBp])

  useEffect(() => { loadAttention() }, [loadAttention])
  useComplianceChanged(loadAttention)
  useRefetchOnFocus(loadAttention)

  useEffect(() => {
    if (!checkIsOnline()) return
    listMyEstates()
      .then(res => setMyEstates(res.data?.data || []))
      .catch(() => setMyEstates([]))
  }, [])

  useEffect(() => {
    if (!complianceEnabled || !hasAnyTaskManagementBp) return
    if (setupPromptedRef.current) return
    if ((attention?.totals?.needsSetup || 0) <= 0) return
    if (isWorkflowRunning()) return
    setupPromptedRef.current = true
    if (window.sessionStorage.getItem('complianceSetupAutoOpened')) return
    window.sessionStorage.setItem('complianceSetupAutoOpened', '1')
    setWizardOpen(true)
  }, [attention, complianceEnabled, hasAnyTaskManagementBp])

  const handleNextStep = step => {
    if (['needs_setup', 'compliance_expired', 'compliance_awaiting_review'].includes(step.key)) {
      setWizardOpen(true)
      return true
    }
    return false
  }

  const wasLoadingRef = useRef(false)
  useEffect(() => {
    if (wasLoadingRef.current && !dashboardLoading) {
      setRenderedScope(scopeKey)
    }
    wasLoadingRef.current = dashboardLoading
  }, [dashboardLoading, scopeKey])

  const isScopeStale = renderedScope !== scopeKey

  const handleAltitudeChange = next => {
    const params = new URLSearchParams(searchParams)
    if (next && next !== 'portfolio') params.set('altitude', next)
    else                              params.delete('altitude')
    params.delete('entityId')
    setSearchParams(params)
  }

  const handleRowDrill = row => {
    if (!row?.entityType || !row?.entityId) return
    const params = new URLSearchParams(searchParams)
    params.set('altitude', row.entityType)
    params.set('entityId', row.entityId)
    params.delete('domain')
    setSearchParams(params)
  }

  const openEntityPage = entity => {
    if (!entity?.id || !entity?.type) return
    const path = entity.type === 'portfolio' ? `/portfolios/${entity.id}`
               : entity.type === 'estate'    ? `/estates/${entity.id}`
               : entity.type === 'bp'         ? `/bp-list/${entity.id}`
               : null
    if (path) navigate(path)
  }

  const handleDrillDown = () => {
    if (!focus) return
    const children = focus.children || {}
    let next = null
    if (focus.type === 'portfolio') {
      next = (children.estates ?? 0) > 0 ? 'estate'
           : (children.buildings ?? 0) > 0 ? 'bp'
           : null
    } else if (focus.type === 'estate') {
      next = (children.buildings ?? 0) > 0 ? 'bp' : null
    } else if (focus.type === 'bp') {
      next = (children.units ?? 0) > 0 ? 'unit' : null
    }
    if (!next) return
    const params = new URLSearchParams(searchParams)
    params.set('altitude', next)
    params.set('entityId', focus.id)
    params.delete('domain')
    setSearchParams(params)
  }

  // Breadcrumb segment click — jump to the entity but stay at its native
  // altitude (so clicking "LDN" goes to portfolio altitude focused on LDN).
  const handleBreadcrumbSelect = target => {
    const params = new URLSearchParams(searchParams)
    if (!target) {
      params.delete('altitude')
      params.delete('entityId')
    } else {
      params.set('altitude', target.type)
      params.set('entityId', target.id)
    }
    params.delete('domain')
    setSearchParams(params)
  }

  // FilterBar dispatcher — updates URL params for: query, status, sort,
  // altitude, entityId. All fields optional; only the keys actually
  // present in the payload are written.
  const handleFilterChange = changes => {
    const params = new URLSearchParams(searchParams)
    if ('query' in changes) {
      if (changes.query) params.set('q', changes.query)
      else               params.delete('q')
    }
    if ('status' in changes) {
      if (changes.status) params.set('s', changes.status)
      else                params.delete('s')
    }
    if ('sort' in changes) {
      if (changes.sort && changes.sort !== 'risk') params.set('o', changes.sort)
      else                                         params.delete('o')
    }
    if ('altitude' in changes) {
      if (changes.altitude && changes.altitude !== 'portfolio') params.set('altitude', changes.altitude)
      else                                                       params.delete('altitude')
    }
    if ('entityId' in changes) {
      if (changes.entityId) params.set('entityId', changes.entityId)
      else                  params.delete('entityId')
    }
    if ('domain' in changes) {
      if (changes.domain) params.set('domain', changes.domain)
      else                params.delete('domain')
    }
    if ('assignee' in changes) {
      if (changes.assignee) params.set('assignee', changes.assignee)
      else                  params.delete('assignee')
    }
    setSearchParams(params)
  }

  const handleClearFocus = () => {
    const params = new URLSearchParams(searchParams)
    params.delete('entityId')
    setSearchParams(params)
  }

  const handleTaskOriginChange = value => {
    const params = new URLSearchParams(searchParams)
    if (value) params.set('origin', value)
    else params.delete('origin')
    setSearchParams(params)
  }

  const handleRangeChange = value => {
    const params = new URLSearchParams(searchParams)
    ;['period', 'from', 'to', 'range'].forEach(k => params.delete(k))
    if (value?.preset === 'custom') {
      const r = resolvePeriod(value)
      if (r.from) { params.set('from', r.from); params.set('to', r.to) }
    } else if (value?.preset && value.preset !== 'last_30_days') {
      params.set('range', value.preset)
    }
    setSearchParams(params)
  }

  const openJumpPicker = (type = null) => {
    setJumpFilterType(type)
    setJumpOpen(true)
  }

  const jumpToEntity = target => {
    if (!target?.type || !target?.id) return
    const params = new URLSearchParams(searchParams)
    if (target.type === 'portfolio') params.delete('altitude')
    else                             params.set('altitude', target.type)
    params.set('entityId', String(target.id))
    params.delete('domain')
    setSearchParams(params)
  }

  const openUnitTracker = unitId => {
    if (!unitId) return
    const params = new URLSearchParams(searchParams)
    params.set('altitude', 'unit')
    params.set('entityId', String(unitId))
    params.delete('domain')
    setSearchParams(params)
  }

  const bandForScore = s => {
    const band = scoreBandFor(s)
    return band ? band.label.replace(/\s+/g, '-') : null
  }

  const taskDomains       = propOr([], 'taskDomains', dashboardStats)
  const activeBpsCount     = propOr(0, 'activeBuildingPassportsCount', dashboardStats)
  const altitudeRows       = propOr([], 'altitudeRows', dashboardStats)
  const altitudeSummary    = propOr({}, 'altitudeSummary', dashboardStats)
  const opsInsights        = propOr(null, 'opsInsights', dashboardStats)
  const buildingsAtRisk    = propOr({ buildings: [], count: 0 }, 'buildingsAtRisk', dashboardStats)
  const permitsData        = propOr({ total: 0, open: 0, expiring: 0, highRiskOpen: 0, byStatus: {}, rows: [] }, 'permits', dashboardStats)
  const contractorsData    = propOr({ total: 0, active: 0, byVetting: {}, docsExpired: 0, docsExpiring: 0, rows: [] }, 'contractors', dashboardStats)
  const contractorPerformanceData = propOr({ totalContractors: 0, rows: [] }, 'contractorPerformance', dashboardStats)
  const recentDocuments    = propOr([], 'recentDocuments', dashboardStats)

  // Average the three component scores (task / action / risk) across the
  // visible rows so the new TripleScore panel can render a per-altitude
  // breakdown with one number per axis.
  const tripleScore = useMemo(() => {
    const avg = (key) => {
      const vs = altitudeRows.map(r => r[key]).filter(v => v !== null && v !== undefined)
      return vs.length ? Math.round(vs.reduce((a, b) => a + b, 0) / vs.length) : null
    }
    return {
      task:   avg('taskScore'),
      action: avg('actionScore'),
      risk:   avg('riskScore'),
    }
  }, [altitudeRows])
  const focus              = propOr(null, 'focus', dashboardStats)
  const kpiInitial = {
    scope: focus?.type === 'unit' && focus?.parentBpId
      ? { type: 'bp', id: focus.parentBpId, label: `The building for ${focus.label}` }
      : ['portfolio', 'estate', 'bp'].includes(focus?.type)
        ? { type: focus.type, id: focus.id, label: focus.label }
        : { type: 'all', id: null, label: 'All Building Passports' },
    period: taskRange,
    origin: taskOrigin,
    assignee: assigneeId ? { id: assigneeId, name: opsInsights?.lens?.name || 'Selected person' } : null,
  }
  const tasksLink = (extra, { dated = true } = {}) => {
    const q = new URLSearchParams({ tab: 'tasks', ...extra })
    const scope = kpiInitial.scope
    if (scope.type === 'bp') q.set('bp', scope.id)
    if (scope.type === 'portfolio') q.set('portfolio', scope.id)
    if (scope.type === 'estate') q.set('estate', scope.id)
    if (dated && resolvedRange.from) { q.set('deadline_from', resolvedRange.from); q.set('deadline_to', resolvedRange.to) }
    if (taskOrigin) q.set('origin', taskOrigin)
    if (assigneeId) q.set('assignee', assigneeId)
    return `${PRIVATE_PATHS.taskManagement}?${q.toString()}`
  }
  const domainBreakdown    = propOr([], 'domainBreakdown', dashboardStats)
  const complianceTrend    = propOr(null, 'complianceTrend', dashboardStats)
  const complianceSummary  = propOr([], 'complianceSummary', dashboardStats)
  const missingObligations = propOr([], 'missingObligations', dashboardStats)

  const headlineProps = useMemo(() => {
    const eyebrow = 'Compliance now'
    if (!focus) {
      return {
        scopeEyebrow: eyebrow,
        scopeLabel: 'All portfolios',
      }
    }
    const row = altitudeRows.find(r => r.entityId === focus.id)
    if (focus.type === 'bp') {
      const bpEntry = complianceSummary.find(e => e.buildingPassportId === focus.id)
      const rollup = bpEntry?.rollup || {}
      return {
        scopeEyebrow: eyebrow,
        scopeLabel: focus.label,
        focusScore: rollup.score,
        focusTask: row?.taskScore,
        focusAction: row?.actionScore,
        focusRisk: row?.riskScore,
        focusSubline: rollup.totalTasks
          ? `${rollup.completedOnTime || 0} satisfied · ${rollup.overdue || 0} overdue · ${(rollup.pending || 0)} pending`
          : 'No obligations scored yet',
      }
    }
    if (focus.type === 'unit') {
      return { scopeEyebrow: eyebrow, scopeLabel: focus.label, focusScore: row?.score }
    }
    const children = focus.children || {}
    const bcount = children.buildings ?? 0
    const ecount = children.estates ?? 0
    return {
      scopeEyebrow: eyebrow,
      scopeLabel: focus.label,
      focusScore: row?.score,
      focusTask: row?.taskScore,
      focusAction: row?.actionScore,
      focusRisk: row?.riskScore,
      focusSubline: bcount > 0
        ? `${bcount} ${bcount === 1 ? 'Building Passport' : 'Building Passports'}${ecount > 0 ? ` across ${ecount} ${ecount === 1 ? 'estate' : 'estates'}` : ''} in scope`
        : 'Empty scope',
    }
  }, [focus, altitudeRows, complianceSummary])

  const templatesStats = useMemo(() => {
    const templates = propOr({}, 'checklistTemplates', dashboardStats)
    if (isNotNilOrEmpty(templates)) {
      return values(templates).map(template => {
        const ranges = { 1: [], 2: [], 3: [], 4: [], 5: [] }
        propOr([], 'percentageValues', template).forEach(p => {
          if (p < 20) ranges[1].push(p)
          else if (p < 50) ranges[2].push(p)
          else if (p < 75) ranges[3].push(p)
          else if (p < 100) ranges[4].push(p)
          else ranges[5].push(p)
        })
        return { ...template, percentageValues: ranges }
      })
    }
    return []
  }, [dashboardStats])

  const portfolioRollup = useMemo(() => {
    if (!complianceSummary.length) return null
    const totals = { totalTasks: 0, completedOnTime: 0, overdue: 0, pending: 0 }
    let critical = 0, attention = 0, onTrack = 0, setCount = 0, scoredBps = 0
    complianceSummary.forEach(entry => {
      const r = entry.rollup || {}
      if (r.score !== null && r.score !== undefined) {
        scoredBps += 1
      }
      totals.totalTasks      += r.totalTasks      ?? 0
      totals.completedOnTime += r.completedOnTime ?? 0
      totals.overdue         += r.overdue         ?? 0
      totals.pending         += r.pending         ?? 0
      setCount += entry.setCount || 0
      if (r.score === null || r.score === undefined) return
      const band = scoreBandFor(r.score)
      if (!band) return
      if (band.level === 'critical' || band.level === 'high') critical += 1
      else if (band.level === 'medium') attention += 1
      else onTrack += 1
    })
    return {
      score: weightedScore(altitudeRows),
      totals, critical, attention, onTrack, setCount,
      bpCount: complianceSummary.length,
      scoredBps,
    }
  }, [complianceSummary, altitudeRows])

  const matrixDomains = useMemo(() => {
    const referenced = new Set()
    altitudeRows.forEach(row => {
      (row.domainBreakdown || []).forEach(d => d.taskDomainId && referenced.add(d.taskDomainId))
    })
    return taskDomains.filter(d => referenced.has(d.id))
  }, [taskDomains, altitudeRows])

  // Visible rows after the FilterBar's search / status / sort have been
  // applied. The raw `altitudeRows` is the unfiltered source of truth so
  // KPI tiles + scope summaries still reflect the full population.
  const visibleAltitudeRows = useMemo(() => {
    const q = (filterQ || '').trim().toLowerCase()
    let rows = altitudeRows.slice()
    if (q) rows = rows.filter(r => (r.entityName || '').toLowerCase().includes(q))
    if (filterStat) rows = rows.filter(r => bandForScore(r.score) === filterStat)

    if (filterSort === 'name') {
      rows.sort((a, b) => (a.entityName || '').localeCompare(b.entityName || ''))
    } else if (filterSort === 'score') {
      rows.sort((a, b) => (a.score ?? 101) - (b.score ?? 101))
    } else {
      // 'risk' (default): most overdue first, then lowest score.
      rows.sort((a, b) =>
        (b.overdue || 0) - (a.overdue || 0)
        || (a.score ?? 101) - (b.score ?? 101))
    }
    return rows
  }, [altitudeRows, filterQ, filterStat, filterSort])

  const matrixRows = useMemo(() =>
    visibleAltitudeRows.map(row => ({
      id: row.entityId,
      entityId: row.entityId,
      entityType: row.entityType,
      name: row.entityName,
      breakdown: (row.domainBreakdown || []).map(d => ({
        task_domain_id: d.taskDomainId,
        score: d.score,
        overdue: d.overdue,
        total_tasks: d.totalTasks,
      })),
    })),
  [visibleAltitudeRows])

  // Apply the same filter/sort to the per-BP card grid so it stays in
  // sync with the matrix.
  const visibleComplianceSummary = useMemo(() => {
    const q = (filterQ || '').trim().toLowerCase()
    let entries = complianceSummary.slice()
    if (q) entries = entries.filter(e => (e.buildingPassportName || '').toLowerCase().includes(q))
    if (filterStat) entries = entries.filter(e => bandForScore(e.rollup?.score) === filterStat)

    if (filterSort === 'name') {
      entries.sort((a, b) => (a.buildingPassportName || '').localeCompare(b.buildingPassportName || ''))
    } else if (filterSort === 'score') {
      entries.sort((a, b) => (a.rollup?.score ?? 101) - (b.rollup?.score ?? 101))
    } else {
      entries.sort((a, b) =>
        (b.rollup?.overdue || 0) - (a.rollup?.overdue || 0)
        || (a.rollup?.score ?? 101) - (b.rollup?.score ?? 101))
    }
    return entries
  }, [complianceSummary, filterQ, filterStat, filterSort])

  const altitudeLabel = altitude === 'portfolio' ? 'Portfolio'
                      : altitude === 'estate'    ? 'Estate'
                      : altitude === 'unit'      ? 'Unit Passport'
                      : 'Building Passport'

  const renderBpBoxes = useCallback(
    bpList => bpList.map(bp => <BpBox key={bp.id} bp={bp} buildingTypes={buildingTypes} />),
    [buildingTypes],
  )

  const recentlyAddedBps = useMemo(() => {
    if (isNotNilOrEmpty(bps)) return renderBpBoxes(bps.slice(0, 3))
    return <DashboardEmptyState style={{ margin: '0 auto' }} />
  }, [bps, renderBpBoxes])

  const lastViewedBps = useMemo(() => {
    if (isNotNilOrEmpty(lastViewed)) return renderBpBoxes(lastViewed.slice(0, 5))
    return null
  }, [lastViewed, renderBpBoxes])

  const upcoming = (dashboardStats?.upcomingEvents || []).filter(e => e.daysUntil >= 0)
  const sparkline = (complianceTrend || []).map(p => p.score === null ? 0 : p.score)
  const trendArrow = sparkline.length >= 2 ? Math.round((sparkline.at(-1) - sparkline[0]) * 10) / 10 : null

  const handleDomainClick = row => {
    const domain = taskDomains.find(d => d.id === row.taskDomainId) || taskDomains.find(d => d.id === row.id) || {}
    setDrawerDomain({
      ...row,
      id: domain.id ?? row.id,
      name: row.name ?? domain.name,
      colour: domain.colour ?? row.colour,
      description: domain.description,
      freq_label: domain.freqLabel ?? row.freqLabel,
    })
  }

  const handleCellClick = ({ row, domain, value }) => {
    setDrawerDomain({
      id: domain.id,
      name: domain.name,
      short: domain.short,
      colour: domain.colour,
      description: domain.description,
      freq_label: domain.freqLabel,
      score: value?.score ?? null,
      overdue: value?.overdue ?? 0,
      bp_count: 1,
      set_count: 1,
    })
  }

  const drawerPerBp = useMemo(() => {
    if (!drawerDomain) return []
    return complianceSummary
      .map(bp => {
        const cell = (bp.domainBreakdown || []).find(d => d.taskDomainId === drawerDomain.id)
        if (!cell) return null
        return {
          building_passport_id: bp.buildingPassportId,
          building_passport_name: bp.buildingPassportName,
          score: cell.score,
          overdue: cell.overdue,
        }
      })
      .filter(Boolean)
  }, [drawerDomain, complianceSummary])

  if (persona === 'team_member') {
    return (
      <PageWrapper>
        <PersonaPrompt />
        <ContentWrapper>
          <PassportsBanner />
          <PageHeaderWrapper>
            <WelcomeHeader data-tour-id='dashboard.welcome'>
              <PageHeader>Welcome back, {currentUserName}!</PageHeader>
              <StatusIndicator />
            </WelcomeHeader>
            <HeaderActions data-tour-id='dashboard.actions'>
              <HeaderTooltip
                title='Go to my Passports'
                body='Open the Building Passports list. Every building you have access to, the same list you used to reach from here.'
              >
                <PassportsBtn onClick={() => navigate('/portfolios?level=bp')}>
                  <ApartmentRoundedIcon style={{ fontSize: 16 }} /> Go to my Passports
                </PassportsBtn>
              </HeaderTooltip>
              <PersonaSwitcher />
            </HeaderActions>
          </PageHeaderWrapper>
          <NextStepsPanel mode={persona} onStep={handleNextStep} />
          <TeamMemberView />
        </ContentWrapper>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      <PersonaPrompt />
      {isLoading || isScopeStale || (dashboardLoading && (!dashboardStats || isEmpty(dashboardStats))) ? (
        <DashboardSkeleton />
      ) : isUnitOnly ? (
        <ContentWrapper>
          {units.length === 1 && !localStorage.getItem('unitWelcomeShown') ? (
            (() => {
              const unit = units[0]
              const bp = unit.buildingPassport
              return (
                <UnitWelcomeCard>
                  <img src={bpLogo} alt='Building Passport' style={{ width: 80, marginBottom: 20, opacity: 0.95 }} />
                  <h2 style={{ fontSize: 22, fontWeight: 600, color: '#0C1229', marginBottom: 10 }}>Welcome to your new Building Passport</h2>
                  <p style={{ fontSize: 16, color: '#555', marginBottom: 30 }}>
                    You've been added to <strong>{unit.name}</strong> of <strong>{bp.name}</strong>.
                  </p>
                  <UnitAccessButton onClick={() => {
                    const bpId = bp?.id || unit.buildingPassportId
                    if (!bpId) return
                    localStorage.setItem('unitWelcomeShown', 'true')
                    navigate(`/bp-list/${bpId}/units/${unit.id}`)
                  }}>Access your Unit Passport</UnitAccessButton>
                </UnitWelcomeCard>
              )
            })()
          ) : (
            <Section>
              <SectionTitle>Your Unit Passports</SectionTitle>
              <BpsList>
                {units.map(unit => (
                  <BpBox key={unit.id} bp={unit.buildingPassport} unit={unit} buildingTypes={buildingTypes} />
                ))}
              </BpsList>
            </Section>
          )}
        </ContentWrapper>
      ) : (
        <ContentWrapper>
          <PassportsBanner />
          <PageHeaderWrapper>
            <WelcomeHeader data-tour-id='dashboard.welcome'>
              <PageHeader>Welcome back, {currentUserName}!</PageHeader>
              <StatusIndicator />
            </WelcomeHeader>
            <HeaderActions data-tour-id='dashboard.actions'>
              <HeaderTooltip
                title='Go to my Passports'
                body='Open the Building Passports list. Every building you manage, the same list you used to reach from here.'
              >
                <PassportsBtn onClick={() => navigate('/portfolios?level=bp')}>
                  <ApartmentRoundedIcon style={{ fontSize: 16 }} /> Go to my Passports
                </PassportsBtn>
              </HeaderTooltip>
              <PersonaSwitcher />
              <HeaderTooltip
                title='Jump to…'
                body='Search across every portfolio, estate, Building Passport and unit you have access to, and go straight there. No need to drill down level by level.'
                shortcut='j'
              >
                <HeaderActionBtn onClick={() => openJumpPicker(null)}>
                  <TravelExploreRoundedIcon style={{ fontSize: 16 }} /> Jump to…
                </HeaderActionBtn>
              </HeaderTooltip>
              {complianceEnabled && hasAnyTaskManagementBp && (
                <HeaderTooltip
                  title='Bulk update'
                  body='Change several obligations at once across many buildings. Mark not applicable, adjust criticality, or set cadence in one step.'
                  shortcut='b'
                >
                  <HeaderActionBtn onClick={() => setBulkOpen(true)}>
                    <TuneRoundedIcon style={{ fontSize: 16 }} /> Bulk update
                  </HeaderActionBtn>
                </HeaderTooltip>
              )}
              {complianceEnabled && hasAnyTaskManagementBp && (
                <HeaderTooltip
                  title='Portfolio report'
                  body='Download a PDF of compliance across your whole portfolio. Average score, top-priority gaps, and a per-building breakdown.'
                >
                  <HeaderActionBtn onClick={() => window.open('/dashboard/report', '_blank')}>
                    <FileDownloadRoundedIcon style={{ fontSize: 16 }} /> Download report
                  </HeaderActionBtn>
                </HeaderTooltip>
              )}
              {complianceEnabled && hasAnyTaskManagementBp && (
                <HeaderTooltip
                  title='KPI report'
                  body='Download a KPI report of on-time completion and remedials by risk rating. It starts with the sites, period, task type and person you are viewing, and you can change any of them.'
                >
                  <HeaderActionBtn onClick={() => setKpiOpen(true)}>
                    <AssessmentRoundedIcon style={{ fontSize: 16 }} /> KPI report
                  </HeaderActionBtn>
                </HeaderTooltip>
              )}
              {kpiOpen && (
                <KpiReportDialog
                  open={kpiOpen}
                  onClose={() => setKpiOpen(false)}
                  initial={kpiInitial}
                />
              )}
              <HeaderTooltip
                title='Keyboard shortcuts'
                body='See every keyboard shortcut for moving around the dashboard.'
                shortcut='?'
              >
                <HeaderActionBtn onClick={() => setShortcutHelpOpen(true)} $compact>
                  <HelpOutlineRoundedIcon style={{ fontSize: 16 }} />
                </HeaderActionBtn>
              </HeaderTooltip>
            </HeaderActions>
          </PageHeaderWrapper>

          <FeatureSwitchBar surface='dashboard' isOwner={featureOwner} />

          <NextStepsPanel mode={persona} onStep={handleNextStep} />

          {!hasAnyTaskManagementBp && <ComplianceInactiveNotice />}

          <ViewTracker
            active={(() => {
              if (!focus) return altitude === 'portfolio' && !entityId ? 'full' : altitude
              if (focus.type === 'unit') return 'unit'
              if (focus.type === 'bp' && altitude === 'unit') return 'unit'
              return focus.type
            })()}
            onSelect={lvl => {
              if (lvl === 'full') {
                handleFilterChange({ altitude: 'portfolio', entityId: null, domain: null })
                return
              }
              const trail = focus?.trail || []
              const ancestor = trail.find(seg => seg.type === lvl)
              if (ancestor) {
                handleFilterChange({ altitude: lvl, entityId: ancestor.id, domain: null })
              } else {
                handleFilterChange({ altitude: lvl, entityId: null, domain: null })
              }
            }}
            onJumpToLevel={lvl => openJumpPicker(lvl === 'full' ? null : lvl)}
            reachable={{
              full:      true,
              portfolio: (altitudeSummary?.portfolio?.count ?? 0) > 0,
              estate:    (altitudeSummary?.estate?.count ?? 0) > 0,
              bp:        (altitudeSummary?.bp?.count ?? 0) > 0,
              unit:      (altitudeSummary?.unit?.count ?? 0) > 0,
            }}
          />

          {complianceEnabled && hasAnyTaskManagementBp && <ComplianceHeadline
            scopeEyebrow={headlineProps.scopeEyebrow}
            scopeLabel={headlineProps.scopeLabel}
            score={focus ? headlineProps.focusScore : portfolioRollup?.score}
            scoreExplain='The weighted share of obligations satisfied. Each obligation is weighted by its criticality, so a critical statutory duty such as fire safety counts far more than a routine best-practice check.'
            trendDelta={focus ? null : trendArrow}
            trendHint='vs 12 months ago'
            trendExplain='Your current compliance score minus your score ~12 months ago, taken from the monthly compliance snapshots. Positive means you have improved over the year.'
            subline={focus ? headlineProps.focusSubline : null}
            metrics={(!focus && portfolioRollup) ? [
              {
                key: 'tracking',
                value: `${portfolioRollup.scoredBps} of ${activeBpsCount || portfolioRollup.bpCount}`,
                label: 'Building Passports scored',
                explain: 'How many of your active Building Passports are set up enough to produce a compliance score. The rest are still being set up. Their obligations have not been reviewed or evidenced yet, so they show as “Setting up” and are excluded from the average rather than counted as 0%.',
              },
              {
                key: 'at-risk',
                value: buildingsAtRisk?.count ?? 0,
                label: 'at risk',
                tone: (buildingsAtRisk?.count ?? 0) > 0 ? 'risk' : undefined,
                explain: 'Building Passports scoring below 75%, OR with one or more overdue obligations. Click to see every at-risk building and exactly why it is flagged.',
                onClick: () => navigate('/dashboard/at-risk'),
              },
              {
                key: 'overdue',
                value: portfolioRollup.totals.overdue,
                label: 'overdue obligations',
                tone: portfolioRollup.totals.overdue > 0 ? 'warn' : undefined,
                explain: 'Obligations across this scope that are past their due date. Open the Remedials list to address them.',
                onClick: () => navigate('/actions?overdue=1'),
              },
            ] : []}
            scoreTask={focus ? headlineProps.focusTask : tripleScore.task}
            scoreAction={focus ? headlineProps.focusAction : tripleScore.action}
            scoreRisk={focus ? headlineProps.focusRisk : tripleScore.risk}
            scoredCount={focus ? undefined : portfolioRollup?.scoredBps}
            totalCount={focus ? undefined : (activeBpsCount || portfolioRollup?.bpCount)}
          />}

          {(() => {
            // Decide which top-of-page view layout to render based on
            // the focus + altitude combination. Full View when nothing
            const showPortfolio = focus?.type === 'portfolio' || focus?.type === 'estate'

            const drillTarget = (() => {
              if (!focus) return null
              const children = focus.children || {}
              if (focus.type === 'bp') {
                return (children.units ?? 0) > 0
                  ? { label: 'Unit Passports', count: children.units }
                  : null
              }
              return null
            })()

            const filterOptions = {
                scope: altitudeRows
                  .filter(r => r.entityId)
                  .map(r => ({ value: r.entityId, label: r.entityName || 'Untitled' })),
                domain: taskDomains.map(d => ({ value: d.id, label: d.name })),
                assignee: (opsInsights?.people || []).map(p => ({ value: p.userId, label: p.name })),
              }

              const renderComplianceMatrix = settings => {
                let rows = matrixRows
                let domains = matrixDomains
                if (settings.scope) {
                  rows = rows.filter(r => r.entityId === settings.scope)
                }
                if (settings.domain) {
                  domains = domains.filter(d => d.id === settings.domain)
                  rows = rows.map(r => ({
                    ...r,
                    breakdown: (r.breakdown || []).filter(b => b.task_domain_id === settings.domain),
                  }))
                }
                return (
                  <ComplianceMatrix
                    rows={rows}
                    domains={domains}
                    onRowFocus={row => handleRowDrill(row)}
                    onCellClick={({ row }) => handleRowDrill(row)}
                  />
                )
              }

              const renderOpsPanel = (panel, settings) => {
                let insights = opsInsights
                let scopeLabel = focus?.label || 'All portfolios'
                if (settings.assignee && opsInsights?.people) {
                  const person = opsInsights.people.find(p => p.userId === settings.assignee)
                  if (person) {
                    insights = { ...opsInsights, people: [person] }
                    scopeLabel = person.name
                  }
                }
                return (
                  <OpsInsightSection
                    panel={panel}
                    scopeLabel={scopeLabel}
                    scopeType={altitude}
                    scopeId={entityId}
                    insights={insights}
                    range={taskRange}
                    rangeText={rangeText}
                    onRangeChange={handleRangeChange}
                    kpiInitial={kpiInitial}
                    multiSite={!(['bp', 'unit'].includes(focus?.type))}
                    origin={taskOrigin}
                    onOriginChange={handleTaskOriginChange}
                    onFocusPerson={userId => navigate(`/tasks?assignee=${userId}`)}
                    onClearLens={() => handleFilterChange({ assignee: null })}
                    assigneeId={assigneeId}
                  />
                )
              }

              const renderOpsInsights = settings => renderOpsPanel(undefined, settings)

              const renderMissingObligations = settings => {
                let items = missingObligations
                if (settings.domain) {
                  items = items.filter(item => item.taskDomainId === settings.domain)
                }
                if (items.length === 0) {
                  return (
                    <EmptyTabState>
                      No missing obligations. Every applicable library item has either evidence or has been marked not applicable.
                    </EmptyTabState>
                  )
                }
                return (
                  <WizardHero>
                    <WizardHeroLeft>
                      <WizardEyebrow>Guided fix</WizardEyebrow>
                      <WizardTitle>
                        {items.length} issues to handle
                      </WizardTitle>
                      <WizardSubtitle>
                        We'll walk you through them one at a time. Each takes about 30 seconds.
                      </WizardSubtitle>
                      <WizardCta onClick={() => setWizardOpen(true)}>
                        Start guided fix →
                      </WizardCta>
                    </WizardHeroLeft>
                    <WizardHeroRight>
                      <WizardPreview>
                        {items.slice(0, 4).map(item => (
                          <WizardPreviewRow key={item.slug}>
                            <WizardPreviewSeverity $tint={severityColour(criticalityLevel(item.criticalityWeight))} />
                            <WizardPreviewName>{item.name}</WizardPreviewName>
                            <WizardPreviewCount>{item.bpCount}×</WizardPreviewCount>
                          </WizardPreviewRow>
                        ))}
                        {items.length > 4 && (
                          <WizardPreviewMore>
                            + {items.length - 4} more queued
                          </WizardPreviewMore>
                        )}
                      </WizardPreview>
                    </WizardHeroRight>
                  </WizardHero>
                )
              }

              const renderUpcomingEvents = settings => {
                let events = upcoming
                if (settings.date_range) {
                  const horizon = Number(settings.date_range)
                  if (!Number.isNaN(horizon)) {
                    events = events.filter(e => e.daysUntil <= horizon)
                  }
                }
                if (events.length === 0) {
                  return (
                    <EmptyTabState>
                      Nothing expiring soon.
                    </EmptyTabState>
                  )
                }
                return (
                  <WidgetStack>
                    <WidgetHead
                      icon={EventRoundedIcon}
                      value={events.length}
                      label={`upcoming event${events.length === 1 ? '' : 's'}`}
                    />
                    <UpcomingEventsScroll>
                    {events.map((event, i) => (
                      <EventCard key={i}>
                        <EventThumbnail onClick={() => { setInsightFile(event); setShowInsightModal(true) }}>
                          {event.thumbnailUrl ? <img src={event.thumbnailUrl} alt='' /> : <InsertDriveFileIcon style={{ color: '#D24724', fontSize: 24, opacity: 0.55 }} />}
                        </EventThumbnail>
                        <EventContent>
                          <EventFileName onClick={() => { setInsightFile(event); setShowInsightModal(true) }}>{event.displayName}</EventFileName>
                          <EventDate>
                            {moment(event.eventDate).format('D MMM YYYY')}{' '}
                            <EventUrgency $urgent={event.daysUntil <= 7}>
                              {event.daysUntil === 0 ? '— Expires today'
                                : event.daysUntil === 1 ? '— Expires tomorrow'
                                : event.daysUntil <= 7 ? `— Expires in ${event.daysUntil} days`
                                : `— ${event.daysUntil} days remaining`}
                            </EventUrgency>
                          </EventDate>
                          {event.buildingPassportName && (
                            <EventBuilding onClick={e => { e.stopPropagation(); handleFilterChange({ altitude: 'bp', entityId: event.buildingPassportId }) }}>
                              {event.buildingPassportName} ↗
                            </EventBuilding>
                          )}
                        </EventContent>
                      </EventCard>
                    ))}
                    </UpcomingEventsScroll>
                  </WidgetStack>
                )
              }

              const renderTrend = () => (complianceTrend ? (
                <TrendLine series={[{
                  name: 'Portfolio average',
                  colour: '#D24724',
                  data: (complianceTrend || []).map(p => ({ date: p.date, score: p.score })),
                }]} />
              ) : (
                <EmptyTabState>
                  Trend builds up as daily snapshots accrue.
                </EmptyTabState>
              ))

              const renderBuildingsAtRisk = () => {
                const list = buildingsAtRisk?.buildings || []
                const total = buildingsAtRisk?.count ?? list.length
                if (list.length === 0) {
                  return (
                    <EmptyTabState>
                      No buildings at risk. Every scored Building Passport is at or above 75% with no overdue obligations.
                    </EmptyTabState>
                  )
                }
                const shown = list.slice(0, visibleFor('buildings_at_risk'))
                return (
                  <WidgetStack>
                    <WidgetHead
                      icon={ReportProblemRoundedIcon}
                      value={total}
                      label={`building${total === 1 ? '' : 's'} at risk`}
                    />
                    <WidgetMetaBar>
                      <WidgetCount
                        title='“At risk” means a Building Passport scoring below 75%, or with one or more overdue obligations. Each row shows exactly why it is flagged.'
                      >
                        Showing {shown.length} of {total}
                      </WidgetCount>
                      <WidgetLink type='button' onClick={() => navigate(PRIVATE_PATHS.buildingsAtRisk)}>
                        View all →
                      </WidgetLink>
                    </WidgetMetaBar>
                    <WidgetScroll>
                      {shown.map(b => (
                        <RiskRow
                          key={b.id}
                          onClick={() => navigate(PRIVATE_PATHS.compliance.replace(':bpId', b.id))}
                          title={`Open the compliance workspace for ${b.name}`}
                        >
                          <RiskScore $tint={scoreColour(b.score)}>
                            {formatPercent(b.score)}
                          </RiskScore>
                          <RiskMain>
                            <RiskName>{b.name}</RiskName>
                            <RiskReasons>
                              {[...(b.reasons || []), [b.city, b.postcode].filter(Boolean).join(', ')]
                                .filter(Boolean).join(' · ')}
                            </RiskReasons>
                          </RiskMain>
                          <RiskArrow aria-hidden='true'>→</RiskArrow>
                        </RiskRow>
                      ))}
                      <InfiniteScrollSentinel
                        hasMore={shown.length < list.length}
                        loading={false}
                        onLoadMore={() => revealMore('buildings_at_risk')}
                      />
                    </WidgetScroll>
                  </WidgetStack>
                )
              }

              const renderContractors = () => {
                const c = contractorsData || {}
                const total = c.total || 0
                if (total === 0) {
                  return (
                    <EmptyTabState>
                      <EmptyIcon aria-hidden='true'>
                        <EngineeringRoundedIcon style={{ fontSize: 30 }} />
                      </EmptyIcon>
                      <EmptyTitle>No contractors {c.scoped ? 'in this view' : 'yet'}</EmptyTitle>
                      <EmptyText>
                        {c.scoped
                          ? 'No contractors are linked to the buildings in this view through a permit, task or incident yet.'
                          : 'Add contractors to track vetting status and keep an eye on insurance, competence and RAMS documents as they approach expiry.'}
                      </EmptyText>
                      <WidgetLink type='button' onClick={() => navigate(PRIVATE_PATHS.contractors)}>
                        {c.scoped ? 'Open contractors →' : 'Add contractors →'}
                      </WidgetLink>
                    </EmptyTabState>
                  )
                }

                const prettyDoc = t => ({
                  insurance: 'Insurance', competence: 'Competence', rams: 'RAMS', other: 'Document',
                }[t] || (t || 'Document').replace(/_/g, ' ').replace(/^./, ch => ch.toUpperCase()))
                const initials = name => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2)
                  .map(w => w[0]?.toUpperCase()).join('') || '?'

                const vetting = c.byVetting || {}
                const vettingEntries = Object.entries(vetting).filter(([, n]) => n > 0)
                const expired = c.docsExpired || 0
                const expiring = c.docsExpiring || 0
                const docs = c.rows || []
                const shown = docs.slice(0, visibleFor('contractors'))
                const expiringTotal = expiring + expired

                return (
                  <WidgetStack>
                    <ContractorHead>
                      <HeadStat>
                        <HeadIcon aria-hidden='true'>
                          <EngineeringRoundedIcon style={{ fontSize: 18 }} />
                        </HeadIcon>
                        <HeadText>
                          <HeadTitle>
                            <HeadNum>{total}</HeadNum>
                            <HeadWord>contractor{total === 1 ? '' : 's'}{c.scoped ? ' in view' : ''}</HeadWord>
                          </HeadTitle>
                          <HeadSub>
                            <ActiveDot $on={(c.active || 0) > 0} aria-hidden='true' />
                            {c.active || 0} active
                          </HeadSub>
                        </HeadText>
                      </HeadStat>
                      {vettingEntries.length > 0 && (
                        <ChipWrap>
                          {vettingEntries.map(([key, count]) => {
                            const m = vettingMeta(key)
                            return (
                              <VetChip key={key} $fg={m.tint} $bg={m.bg} title={`${m.label}: ${count}`}>
                                {m.label} · {count}
                              </VetChip>
                            )
                          })}
                        </ChipWrap>
                      )}
                    </ContractorHead>

                    <StatRow>
                      <StatPill $tone={expiring > 0 ? 'warn' : 'muted'}>
                        <StatNum>{expiring}</StatNum><StatLbl>Docs expiring ≤ 30d</StatLbl>
                      </StatPill>
                      <StatPill $tone={expired > 0 ? 'bad' : 'muted'}>
                        <StatNum>{expired}</StatNum><StatLbl>Docs expired</StatLbl>
                      </StatPill>
                    </StatRow>

                    {docs.length > 0 ? (
                      <>
                        <WidgetMetaBar>
                          <WidgetCount>Showing {shown.length} of {expiringTotal} needing attention</WidgetCount>
                          <WidgetLink type='button' onClick={() => navigate(PRIVATE_PATHS.contractors)}>
                            All contractors →
                          </WidgetLink>
                        </WidgetMetaBar>
                        <WidgetScroll>
                          {shown.map(d => (
                            <DocRow
                              key={d.id}
                              $clickable
                              onClick={() => navigate(PRIVATE_PATHS.contractors)}
                              title={`Open ${d.contractorName} in the contractors area`}
                            >
                              <DocAvatar $tint={d.state === 'expired' ? '#b00020' : '#d97706'} aria-hidden='true'>
                                {initials(d.contractorName)}
                              </DocAvatar>
                              <DocMain>
                                <DocName>{d.contractorName}</DocName>
                                <DocMeta>{prettyDoc(d.docType)} · {d.displayName}</DocMeta>
                              </DocMain>
                              <ExpiryTag $expired={d.state === 'expired'}>
                                {d.state === 'expired' ? 'Expired' : 'Expires'} {moment(d.expiresAt).format('D MMM YYYY')}
                              </ExpiryTag>
                            </DocRow>
                          ))}
                          <InfiniteScrollSentinel
                            hasMore={shown.length < docs.length}
                            loading={false}
                            onLoadMore={() => revealMore('contractors')}
                          />
                        </WidgetScroll>
                      </>
                    ) : (
                      <AllClear>
                        <AllClearDot aria-hidden='true' />
                        All contractor documents are in date.
                        <WidgetLink type='button' onClick={() => navigate(PRIVATE_PATHS.contractors)}>
                          All contractors →
                        </WidgetLink>
                      </AllClear>
                    )}
                  </WidgetStack>
                )
              }

              const renderPermits = () => {
                const p = permitsData || {}
                if ((p.total || 0) === 0) {
                  return (
                    <EmptyTabState>
                      No permits to work raised yet.
                    </EmptyTabState>
                  )
                }
                const prettyStatus = s => (s || '').replace(/_/g, ' ').replace(/^./, ch => ch.toUpperCase())
                const rows = p.rows || []
                const shown = rows.slice(0, visibleFor('permits'))
                return (
                  <WidgetStack>
                    <WidgetHead
                      icon={BadgeRoundedIcon}
                      value={p.open || 0}
                      label={`open permit${(p.open || 0) === 1 ? '' : 's'}`}
                      sub={(p.highRiskOpen || 0) > 0
                        ? <><ActiveDot $on={false} style={{ background: '#b00020' }} aria-hidden='true' />{p.highRiskOpen} high-risk</>
                        : null}
                    />
                    <StatRow>
                      <StatPill><StatNum>{p.open || 0}</StatNum><StatLbl>Open</StatLbl></StatPill>
                      <StatPill $tone={(p.highRiskOpen || 0) > 0 ? 'bad' : 'neutral'}><StatNum>{p.highRiskOpen || 0}</StatNum><StatLbl>High-risk open</StatLbl></StatPill>
                      <StatPill $tone={(p.expiring || 0) > 0 ? 'warn' : 'neutral'}><StatNum>{p.expiring || 0}</StatNum><StatLbl>Expiring ≤ 7d</StatLbl></StatPill>
                    </StatRow>
                    {rows.length > 0 && (
                      <>
                        <WidgetMetaBar>
                          <WidgetCount>Showing {shown.length} of {p.open || rows.length} open</WidgetCount>
                        </WidgetMetaBar>
                        <WidgetScroll>
                          {shown.map(row => (
                            <PermitRow
                              key={row.id}
                              $clickable={!!row.buildingPassportId}
                              onClick={() => row.buildingPassportId && navigate(PRIVATE_PATHS.permits.replace(':bpId', row.buildingPassportId))}
                              title={row.buildingPassportName ? `Open permits for ${row.buildingPassportName}` : undefined}
                            >
                              <PermitMain>
                                <PermitTitle>
                                  {row.isHighRisk && <HighRiskDot title='High-risk activity' aria-hidden='true' />}
                                  {row.title}
                                </PermitTitle>
                                <PermitMeta>
                                  {[row.activityTypeLabel, row.contractor, row.buildingPassportName].filter(Boolean).join(' · ')}
                                </PermitMeta>
                              </PermitMain>
                              <PermitStatusTag>{prettyStatus(row.status)}</PermitStatusTag>
                            </PermitRow>
                          ))}
                          <InfiniteScrollSentinel
                            hasMore={shown.length < rows.length}
                            loading={false}
                            onLoadMore={() => revealMore('permits')}
                          />
                        </WidgetScroll>
                      </>
                    )}
                  </WidgetStack>
                )
              }

              const SEV_TINT = { critical: RAG.critical.fg, high: RAG.high.fg, medium: RAG.medium.fg, low: RAG.low.fg }
              const sevItems = map => ['critical', 'high', 'medium', 'low'].map(k => ({
                key: k, label: k.charAt(0).toUpperCase() + k.slice(1), tint: SEV_TINT[k],
                count: (map || {})[k] || (map || {})[k?.toUpperCase()] || 0,
              }))

              const renderComplianceGauge = () => {
                const scored = complianceSummary.filter(e => e.rollup?.score !== null && e.rollup?.score !== undefined)
                const total = complianceSummary.length
                const agg = weightedScore(altitudeRows)
                const provisional = scored.length > 0 && scored.length < total && (scored.length / total) < 0.5
                const sub = scored.length > 0
                  ? `${scored.length} of ${total} Building Passport${total === 1 ? '' : 's'} scored`
                  : 'No buildings scored yet. Finish compliance setup to see a score'
                return <RagGauge value={agg} label='Compliance score' sublabel={sub} provisional={provisional} />
              }

              const renderOntimeGauge = () => (
                <RagGauge value={opsInsights?.tasks?.onTimeRate} label='On-time completion' sublabel='tasks completed by their deadline' target={90} />
              )

              const renderObligationsStatus = () => {
                let satisfied = 0; let pending = 0; let overdue = 0
                complianceSummary.forEach(e => { const r = e.rollup || {}; satisfied += r.completedOnTime || 0; pending += r.pending || 0; overdue += r.overdue || 0 })
                return <RagBreakdown emptyLabel='No obligations tracked in this view yet.' items={[
                  { key: 'satisfied', label: 'Satisfied', tint: '#15803d', count: satisfied },
                  { key: 'pending', label: 'Awaiting evidence', tint: '#d97706', count: pending },
                  { key: 'overdue', label: 'Overdue', tint: '#b00020', count: overdue },
                ]} />
              }

              const dashboardRemedialsUrl = (view, extra = {}) => {
                if (altitude === 'bp' && entityId) return remedialsViewUrl({ bpId: entityId }, view, extra)
                if ((altitude === 'portfolio' || altitude === 'estate') && entityId) return remedialsViewUrl({}, view, { [altitude]: entityId, ...extra })
                return remedialsViewUrl({}, view, extra)
              }

              const renderRemedialsSeverity = () => (
                <>
                  <RagBreakdown
                    emptyLabel='No open remedials in this view.'
                    items={sevItems(opsInsights?.actions?.bySeverity).map(item => ({ ...item, onClick: () => navigate(dashboardRemedialsUrl('all_open', { severity: item.key })) }))}
                  />
                  <WidgetNote>Open remedials only, each severity counted on its own. Remedials awaiting review are not counted until someone checks them. Click a level to see those remedials.</WidgetNote>
                </>
              )

              const renderIncidentsSeverity = () => (
                <RagBreakdown emptyLabel='No incidents logged in this view.' items={sevItems(opsInsights?.incidents?.bySeverity)} />
              )

              const renderBuildingsRag = () => {
                const buckets = { low: 0, medium: 0, high: 0, critical: 0 }
                let inSetup = 0
                complianceSummary.forEach(e => {
                  const b = scoreBandFor(e.rollup?.score)
                  if (b) buckets[b.level]++
                  else inSetup++
                })
                return <RagBreakdown
                  emptyLabel='Counts your Building Passports by compliance band (green / amber / red) once they have a score.'
                  items={[
                    { key: 'low', label: 'On track (75%+)', tint: '#15803d', count: buckets.low },
                    { key: 'medium', label: 'Attention (50–74%)', tint: '#d97706', count: buckets.medium },
                    { key: 'high', label: 'At risk (20–49%)', tint: '#c2410c', count: buckets.high },
                    { key: 'critical', label: 'Critical (<20%)', tint: '#b00020', count: buckets.critical },
                    { key: 'setup', label: 'In setup. Not scored yet', tint: '#94a3b8', count: inSetup },
                  ]} />
              }

              const renderAwaitingApproval = () => {
                const s = opsInsights?.tasks?.byStatus || {}
                return <RagBreakdown emptyLabel='Nothing needs action right now. No tasks awaiting approval, on hold, or overdue.' items={[
                  { key: 'awaiting_approval', label: 'Awaiting sign-off', tint: '#7C5FE0', count: s.awaitingApproval || 0 },
                  { key: 'on_hold', label: 'On hold', tint: '#d97706', count: s.onHold || 0 },
                  { key: 'overdue', label: 'Overdue', tint: '#b00020', count: opsInsights?.tasks?.overdue || 0 },
                ]} />
              }

              const renderOverdueAgeing = () => {
                const a = opsInsights?.tasks?.overdueAgeing || {}
                return <RagBreakdown emptyLabel='No overdue tasks in this view. Good.' items={[
                  { key: 'recent', label: 'Overdue under 1 week', tint: '#d97706', count: a.recent || 0 },
                  { key: 'mid', label: 'Overdue 1–4 weeks', tint: '#c2410c', count: a.mid || 0 },
                  { key: 'old', label: 'Overdue over 1 month', tint: '#b00020', count: a.old || 0 },
                ]} />
              }

              const renderUnassigned = () => {
                const n = opsInsights?.tasks?.unassigned || 0
                return <StatCard
                  value={n}
                  label='Unassigned open tasks'
                  tint={n > 0 ? '#c2410c' : '#15803d'}
                  sub={n > 0 ? 'Open tasks with nobody responsible. Give each an owner so it gets done.' : 'Every open task has an owner.'}
                  onClick={() => navigate(tasksLink({ status: 'unassigned' }))}
                />
              }

              const renderOntimeTrend = () => <TrendBars points={opsInsights?.taskTrend || []} />

              const renderPeriodDelta = () => <PeriodDelta trend={opsInsights?.taskTrend || []} />

              const renderDueThisWeek = () => (
                <TaskRowList tasks={opsInsights?.upcomingTasks || []} variant='upcoming' onOpen={id => navigate(PRIVATE_PATHS.taskDetails.replace(':id', id))} />
              )

              const renderMostOverdue = () => (
                <TaskRowList tasks={opsInsights?.overdueTasks || []} variant='overdue' onOpen={id => navigate(PRIVATE_PATHS.taskDetails.replace(':id', id))} />
              )

              const renderTopPerformers = () => {
                const ppl = (opsInsights?.people || []).filter(p => (p.tasksCompleted || 0) > 0)
                const rows = [...ppl].sort((a, b) => (b.onTimeRate ?? -1) - (a.onTimeRate ?? -1)).slice(0, 6).map(p => ({
                  key: p.userId, name: p.name, score: p.onTimeRate,
                  sub: `${p.tasksCompleted} done · ${p.tasksOverdue || 0} overdue`,
                  onClick: () => navigate(`/tasks?assignee=${p.userId}`),
                }))
                return <ScoreList rows={rows} emptyLabel='No completed tasks by anyone yet.' />
              }

              const renderTaskCompletion = () => (
                <TaskCompletionCard tasks={opsInsights?.tasks || {}} rangeLabel={rangeText} />
              )

              const renderTaskProgressMix = () => {
                const mix = opsInsights?.tasks?.progressMix || {}
                const days = mix.dueSoonDays ?? opsInsights?.tasks?.dueSoonDays ?? 7
                const counts = {
                  upcoming: mix.upcoming ?? 0,
                  dueSoon: mix.dueSoon ?? 0,
                  overdue: mix.overdue ?? 0,
                  onHold: mix.onHold ?? 0,
                  completedLate: mix.completedLate ?? 0,
                  completedOnTime: mix.completedOnTime ?? 0,
                }
                const drill = taskMixDrill(days)
                return (
                  <ProgressMixCard
                    title='Task progress mix'
                    lead='Every task on these sites, counted once. This one ignores the dates above so you can see work that is still ahead of you.'
                    centreLabel='Tasks'
                    emptyText='No tasks on these sites yet'
                    buckets={taskBucketsWithWindow(days).map(b => ({ ...b, count: counts[b.key] }))}
                    onOpen={seg => navigate(tasksLink(drill[seg.key] || {}, { dated: false }))}
                  />
                )
              }

              const renderRemedialProgressMix = () => {
                const a = opsInsights?.actions || {}
                const allOpen = a.views?.allOpen ?? 0
                const overdue = a.overdue ?? 0
                const counts = {
                  openOnTrack: Math.max(0, allOpen - overdue),
                  overdue,
                  resolvedOnTime: a.views?.recentlyResolved ?? 0,
                  resolvedLate: 0,
                  resolvedNoDate: 0,
                }
                const lastThirtyDays = { from: moment().subtract(30, 'days').format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') }
                const drill = {
                  openOnTrack: ['all_open', {}],
                  overdue: ['overdue', {}],
                  resolvedOnTime: ['resolved', resolvedDates('resolved', lastThirtyDays)],
                }
                return (
                  <ProgressMixCard
                    title='Remedial progress mix'
                    lead={REMEDIAL_MIX_LEAD}
                    centreLabel='Remedials'
                    emptyText='No remedials in this view'
                    buckets={REMEDIAL_PROGRESS_BUCKETS
                      .filter(b => b.key !== 'resolvedLate' && b.key !== 'resolvedNoDate' && b.key !== 'dismissedAfterApproval')
                      .map(b => ({
                        ...b,
                        label: b.key === 'resolvedOnTime' ? 'Resolved, last 30 days' : b.label,
                        help: b.key === 'resolvedOnTime'
                          ? 'Put right and closed in the last 30 days.'
                          : b.key === 'openOnTrack'
                            ? `${b.help} Clicking opens every open remedial, including the overdue ones.`
                            : b.help,
                        count: counts[b.key],
                      }))}
                    onOpen={seg => navigate(dashboardRemedialsUrl(...(drill[seg.key] || ['all_open', {}])))}
                  />
                )
              }

              const renderTasksByPriority = () => (
                <TasksByPriorityCard
                  byPriority={opsInsights?.tasks?.byPriority || []}
                  rangeLabel={rangeText}
                  onOpenPriority={k => navigate(tasksLink({ priority: k }))}
                />
              )

              const renderDomainActivity = (settings = {}) => {
                let domains = opsInsights?.tasks?.byDomain || []
                if (settings.domain) domains = domains.filter(d => d.domainId === settings.domain)
                return (
                  <DomainActivity
                    domains={domains}
                    onOpenDomain={d => navigate(`/tasks?domain=${d.domainId}`)}
                  />
                )
              }

              const renderRecentDocuments = () => {
                const docs = recentDocuments || []
                if (docs.length === 0) {
                  return (
                    <EmptyTabState>
                      No documents uploaded yet.
                    </EmptyTabState>
                  )
                }
                const shown = docs.slice(0, visibleFor('recent_documents'))
                return (
                  <WidgetStack>
                    <WidgetHead
                      icon={DescriptionRoundedIcon}
                      value={docs.length}
                      label='recent documents'
                    />
                    <WidgetMetaBar>
                      <WidgetCount title={focus?.type === 'unit'
                        ? 'The most recent files for this Unit Passport. Its own and shared files, plus building-wide documents.'
                        : 'The most recently uploaded files across the Building Passports in this view.'}>
                        Latest {shown.length} of {docs.length}{focus?.type === 'unit' ? ' · this Unit Passport + building-wide' : ''}
                      </WidgetCount>
                    </WidgetMetaBar>
                    <WidgetScroll>
                      {shown.map(d => (
                        <DocRow
                          key={d.fileId}
                          $clickable
                          onClick={() => { if (d.fileId) { setInsightFile(d); setShowInsightModal(true) } }}
                          title={`Open insight for ${d.displayName}`}
                        >
                          <DocIcon aria-hidden='true'>
                            <InsertDriveFileIcon style={{ fontSize: 20, color: '#D24724', opacity: 0.6 }} />
                          </DocIcon>
                          <DocMain>
                            <DocName>{d.displayName}</DocName>
                            <DocMeta>
                              {[d.fileType || 'Document', d.buildingPassportName, d.createdAt ? moment(d.createdAt).fromNow() : null]
                                .filter(Boolean).join(' · ')}
                            </DocMeta>
                          </DocMain>
                        </DocRow>
                      ))}
                      <InfiniteScrollSentinel
                        hasMore={shown.length < docs.length}
                        loading={false}
                        onLoadMore={() => revealMore('recent_documents')}
                      />
                    </WidgetScroll>
                  </WidgetStack>
                )
              }

              const fullNodes = (widgetKey, settings = {}) => {
                switch (widgetKey) {
                  case 'compliance_matrix': return renderComplianceMatrix(settings)
                  case 'compliance_trend': return renderTrend(settings)
                  case 'ops_insights': return renderOpsInsights(settings)
                  case 'tasks_overview': return renderOpsPanel('tasks', settings)
                  case 'actions_overview': return renderOpsPanel('actions', settings)
                  case 'incidents_overview': return renderOpsPanel('incidents', settings)
                  case 'people_overview': return renderOpsPanel('people', settings)
                  case 'missing_obligations': return renderMissingObligations(settings)
                  case 'buildings_at_risk': return renderBuildingsAtRisk(settings)
                  case 'contractors': return renderContractors(settings)
                  case 'contractor_performance': return (
                    <ContractorPerformanceWidget
                      data={contractorPerformanceData}
                      scoped={!!contractorPerformanceData.scoped}
                      onOpen={() => navigate(PRIVATE_PATHS.contractors)}
                    />
                  )
                  case 'permits': return renderPermits(settings)
                  case 'recent_documents': return renderRecentDocuments(settings)
                  case 'domain_activity': return renderDomainActivity(settings)
                  case 'task_completion': return renderTaskCompletion()
                  case 'tasks_by_priority': return renderTasksByPriority()
                  case 'task_progress_mix': return renderTaskProgressMix()
                  case 'remedial_progress_mix': return renderRemedialProgressMix()
                  case 'compliance_gauge': return renderComplianceGauge()
                  case 'ontime_gauge': return renderOntimeGauge()
                  case 'obligations_status': return renderObligationsStatus()
                  case 'remedials_severity': return renderRemedialsSeverity()
                  case 'incidents_severity': return renderIncidentsSeverity()
                  case 'buildings_rag': return renderBuildingsRag()
                  case 'awaiting_approval': return renderAwaitingApproval()
                  case 'top_performers': return renderTopPerformers()
                  case 'overdue_ageing': return renderOverdueAgeing()
                  case 'unassigned_tasks': return renderUnassigned()
                  case 'ontime_trend': return renderOntimeTrend()
                  case 'period_delta': return renderPeriodDelta()
                  case 'due_this_week': return renderDueThisWeek()
                  case 'most_overdue': return renderMostOverdue()
                  case 'upcoming_events': return renderUpcomingEvents(settings)
                  default: return undefined
                }
              }
              const widgetGrid = (
                <CustomisableDashboard
                  altitude={SHARED_LAYOUT_KEY}
                  nodes={fullNodes}
                  filterOptions={filterOptions}
                  hiddenWidgetKeys={[...(!hasAnyTaskManagementBp ? [...COMPLIANCE_WIDGET_KEYS, ...COMPLIANCE_ONLY_WIDGET_KEYS, ...TASK_WIDGET_KEYS] : (complianceEnabled ? [] : [...COMPLIANCE_WIDGET_KEYS, ...COMPLIANCE_ONLY_WIDGET_KEYS])), ...hiddenWidgetKeysFor(featureVisibility, featureOwner)]}
                />
              )

              let topView = null
            if (hasAnyTaskManagementBp && showPortfolio) {
              const focusRow = altitudeRows.find(r => r.entityId === focus.id) || {}
              const domainByEntity = altitudeRows.map(r => ({
                id: r.entityId,
                name: r.entityName,
                bpCount: r.totalTasks > 0 ? 1 : 0,
                domains: r.domainBreakdown,
                entityType: r.entityType,
              }))
              topView = (
                <PortfolioView
                  portfolioName={focus.label}
                  entityType={focus.type}
                  entityId={focus.id}
                  trail={focus.trail || []}
                  onTrailJump={handleBreadcrumbSelect}
                  portfolioMeta={{
                    eyebrow: focus.type === 'estate' ? 'Estate' : 'Portfolio',
                    line: `${altitudeRows.length} ${altitudeRows.length === 1 ? 'entity' : 'entities'} in scope`,
                  }}
                  score={focusRow.score}
                  kpis={{
                    buildings:   altitudeRows.length,
                    atRisk:      altitudeRows.filter(r => (r.score ?? 100) < 75 || r.overdue > 0).length,
                    accountable: null,
                  }}
                  domainByEntity={domainByEntity}
                  domains={matrixDomains}
                  metadata={focus.metadata || {}}
                  drillTarget={drillTarget}
                  onDrillDown={handleDrillDown}
                  onDrill={entity => handleRowDrill({ entityType: entity.entityType, entityId: entity.id })}
                />
              )
            }
            if (hasAnyTaskManagementBp && focus?.type === 'unit') {
              const bpTrailSeg = (focus.trail || []).find(seg => seg.type === 'bp')
              const parentBpId = focus.parentBpId || bpTrailSeg?.id || null
              const parentBpName = (parentBpId
                ? (complianceSummary.find(e => e.buildingPassportId === parentBpId)?.buildingPassportName
                   || (bps || []).find(b => b && b.id === parentBpId)?.name)
                : null)
                || bpTrailSeg?.label
                || ''
              topView = (
                <UnitComplianceView
                  bpId={parentBpId}
                  unitId={focus.unitId || focus.id}
                  unitName={focus.label}
                  bpName={parentBpName}
                  onBack={parentBpId ? () => handleFilterChange({ altitude: 'unit', entityId: parentBpId }) : undefined}
                />
              )
            } else if (hasAnyTaskManagementBp && focus?.type === 'bp' && altitude === 'unit') {
              const domainFilterId = searchParams.get('domain') || null
              const focusedDomain = domainFilterId
                ? (taskDomains || []).find(d => d.id === domainFilterId)
                : null
              topView = (
                <UnitView
                  parentBpName={focus.label}
                  domainFilter={focusedDomain}
                  onClearDomainFilter={() => handleFilterChange({ domain: null })}
                  onOpenUnit={openUnitTracker}
                  units={altitudeRows.map(r => ({
                    id:           r.entityId,
                    name:         r.entityName,
                    plotNumber:   r.plotNumber,
                    floorNumbers: r.floorNumbers,
                    score:        r.score,
                    overdue:      r.overdue,
                    hasTaskData:  r.hasTaskData,
                    domainBreakdown: r.domainBreakdown || [],
                  }))}
                />
              )
            }
            if (hasAnyTaskManagementBp && focus?.type === 'bp' && altitude !== 'unit') {
              const bpEntry = complianceSummary.find(e => e.buildingPassportId === focus.id)
              const bpRollup = bpEntry?.rollup || {}
              const domains = taskDomains
                .filter(d => (bpEntry?.domainBreakdown || []).some(b => b.taskDomainId === d.id))
                .map(d => {
                  const b = (bpEntry?.domainBreakdown || []).find(x => x.taskDomainId === d.id)
                  return {
                    id:      d.id,
                    name:    d.name,
                    short:   d.short,
                    icon:    d.icon,
                    colour:  d.colour,
                    score:   b?.score,
                    overdue: b?.overdue,
                    totalTasks: b?.totalTasks,
                  }
                })
              const unitCount = altitude === 'unit' ? altitudeRows.length
                                : (focus?.children?.units ?? 0)
              topView = (
                <BpView
                  bpId={focus.id}
                  bpName={focus.label}
                  trail={focus.trail || []}
                  onTrailJump={handleBreadcrumbSelect}
                  bpMeta={focus.metadata || {}}
                  score={bpRollup.score}
                  kpis={{
                    overdue: bpRollup.overdue || 0,
                    units:   unitCount,
                  }}
                  domains={domains}
                  missingObligations={missingObligations}
                  hasUnits={unitCount > 0}
                  drillTarget={drillTarget}
                  onDrillDown={handleDrillDown}
                  opsInsights={opsInsights}
                  openObligations={missingObligations.length}
                  onToggleGapDismissed={async gap => {
                    try {
                      if (gap.isDismissed) {
                        await clearObligationOverride({ buildingPassportId: focus.id, librarySlug: gap.slug })
                      } else {
                        await setObligationOverride({ buildingPassportId: focus.id, librarySlug: gap.slug, status: 'acknowledged' })
                      }
                      notifyComplianceChanged()
                    } catch (e) {
                      toast.error('Could not update this item.')
                    }
                  }}
                  onDrillDomain={d => handleFilterChange({ altitude: 'unit', entityId: focus.id, domain: d.id })}
                  onDrillUnits={() => handleFilterChange({ altitude: 'unit', entityId: focus.id })}
                />
              )
            }
            // Entity navigator (always available, compliance on or off). Real
            // drillable rows (portfolios / estates / BPs) plus — crucially —
            // the individual BPs hiding inside the synthetic, non-drillable
            // "Unassigned buildings" row, so users with no portfolio of their
            // own (e.g. agents) can still reach their buildings.
            const bpNameById = {}
            ;(bps || []).forEach(b => { if (b && b.id) bpNameById[b.id] = b.name })
            complianceSummary.forEach(e => { bpNameById[e.buildingPassportId] = e.buildingPassportName })
            const drillRows = altitudeRows.filter(r => r.entityId && r.entityType && !r.isUnassigned)
            const unassignedRow = altitudeRows.find(r => r.isUnassigned)
            const orphanBps = (unassignedRow?.childBpIds || [])
              .map(id => ({ id, type: 'bp', name: bpNameById[id] || 'Building Passport' }))
            const drillIds = new Set(drillRows.map(r => r.entityId))
            const standaloneEstates = myEstates
              .filter(e => !e.portfolio && !drillIds.has(e.id))
              .map(e => ({ id: e.id, type: 'estate', name: e.name || 'Untitled estate' }))
            const navEntities = !entityId
              ? [
                  ...drillRows.map(r => ({ id: r.entityId, type: r.entityType, name: r.entityName || 'Untitled' })),
                  ...standaloneEstates,
                  ...orphanBps,
                ]
              : []

            return (
              <>
                {navEntities.length > 0 && (
                  <PortfolioNavigator
                    label={altitudeLabel}
                    entities={navEntities}
                    onOpen={e => handleRowDrill({ entityType: e.type, entityId: e.id })}
                    onManage={openEntityPage}
                  />
                )}
                {topView}
                {widgetGrid}
              </>
            )
          })()}

          {/* The 3-tab navigator (To do today / Properties / Trends) was
              removed. It duplicated what the ViewTracker already shows
              and added vertical noise without changing the page's purpose.
              The Action items, per-building cards, and Trends now show
              under the active view based on focus state. */}

          {complianceEnabled && hasAnyTaskManagementBp && (
            <ComplianceSetup
              open={wizardOpen}
              onClose={() => setWizardOpen(false)}
            />
          )}

          {complianceEnabled && hasAnyTaskManagementBp && !wizardOpen && (attention?.totals?.needsSetup || 0) > 0 && (
            <ComplianceSetupBanner onOpen={() => setWizardOpen(true)} />
          )}

          <AvatarPromptBanner />

          <BulkActionWizard
            open={bulkOpen && complianceEnabled && hasAnyTaskManagementBp}
            missingObligations={missingObligations}
            onClose={() => setBulkOpen(false)}
            onApplied={() => notifyComplianceChanged()}
          />

          <ShortcutHelp open={shortcutHelpOpen} onClose={() => setShortcutHelpOpen(false)} />

          <JumpToPicker
            open={jumpOpen}
            filterType={jumpFilterType}
            onClose={() => setJumpOpen(false)}
            onSelect={jumpToEntity}
          />

          <OnboardingWizard
            open={onboardingOpen}
            onClose={() => setOnboardingOpen(false)}
            onStartGuidedFix={() => { setOnboardingOpen(false); setWizardOpen(true) }}
          />

        </ContentWrapper>
      )}


      <TaskDomainDrawer
        open={!!drawerDomain}
        domain={drawerDomain}
        perBp={drawerPerBp}
        onClose={() => setDrawerDomain(null)}
      />

      {insightFile && (
        <GuruInsightModal
          open={showInsightModal}
          onClose={() => { setShowInsightModal(false); setInsightFile(null) }}
          file={{ id: insightFile.fileId, displayName: insightFile.displayName }}
          bpId={insightFile.buildingPassportId}
          buildingPassportName={insightFile.buildingPassportName}
        />
      )}
    </PageWrapper>
  )
}

export default Dashboard

const pulse = keyframes`
  0% { opacity: .55; }
  50% { opacity: 1; }
  100% { opacity: .55; }
`

const PageWrapper = styled.div`
  display: flex;
  @media (max-width: 1024px) { flex-direction: column; }
`

const ContentWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  @media (max-width: 1024px) { flex: 1; padding-bottom: 10px; }
  @media (max-width: ${bp.mobile}) { padding-bottom: calc(var(--bp-float-1) + 56px); }
`

const PageHeaderWrapper = styled.div`
  margin-bottom: 18px;
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  flex-wrap: wrap;
  @media (max-width: ${bp.mobile}) { gap: 12px; margin-bottom: 14px; }
`
const WelcomeHeader = styled.div`
  display: flex; align-items: center; gap: 8px; min-width: 0; max-width: 100%;
  @media (max-width: ${bp.mobile}) { flex: 1 1 100%; }
`

const HeaderActions = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  margin-left: auto;
  flex-wrap: wrap;
  @media (max-width: ${bp.mobile}) {
    margin-left: 0; width: 100%; gap: 8px 6px;
  }
`

const HeaderActionBtn = styled.button`
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${({ $compact }) => $compact ? '8px 10px' : '8px 14px'};
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${line.base};
  color: ${({ theme }) => theme.colors.haiti};
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  white-space: nowrap;
  transition: all 160ms ease;
  svg { color: ${({ theme }) => theme.colors.secondary}; transition: color 160ms ease; }
  &:hover {
    background: ${({ theme }) => theme.colors.haiti};
    color: #fff;
    border-color: ${({ theme }) => theme.colors.haiti};
    svg { color: #fff; }
  }

  @media (max-width: ${bp.mobile}) {
    flex: 1 1 auto;
    justify-content: center;
    min-height: 40px;
    padding: ${({ $compact }) => $compact ? '9px 12px' : '9px 14px'};
  }
`

const PassportsBtn = styled.button`
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.secondary};
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  color: #fff;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.02em;
  cursor: pointer;
  white-space: nowrap;
  transition: filter 160ms ease;
  svg { color: #fff; }
  &:hover { filter: brightness(1.06); }

  @media (max-width: ${bp.mobile}) {
    flex: 1 1 100%;
    justify-content: center;
    padding: 11px 16px;
    font-size: 13px;
  }
`

const TipInner = styled.div`
  padding: 10px 12px;
  text-align: left;
`
const TipTitle = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  font-size: 12.5px; font-weight: 800; color: #fff; margin-bottom: 3px;
`
const TipKey = styled.span`
  font-size: 10px; font-weight: 700; line-height: 1;
  padding: 3px 6px; border-radius: 5px;
  background: rgba(255,255,255,0.16); color: #fff;
  border: 1px solid rgba(255,255,255,0.22);
`
const TipBody = styled.div`
  font-size: 11.5px; line-height: 1.5; color: #c9cede; font-weight: 500;
`

const StatusIndicator = styled.span`
  width: 10px; height: 10px; border-radius: 50%;
  background: ${({ theme }) => theme.colors.secondary};
  margin-left: 16px;
  animation: ${pulse} 2s infinite;
`

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 28px;
  @media (max-width: 1024px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: ${bp.mobile})  { grid-template-columns: 1fr; }
`

const Section = styled.div`
  margin-bottom: 28px;
`

const EmptyAltitude = styled.div`
  padding: 24px;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px dashed ${line.base};
  border-radius: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[800]};
  text-align: center;
`

const TripleScoreStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin: 16px 0 24px;
  @media (max-width: ${bp.mobile}) { grid-template-columns: 1fr; }
`

const TripleScoreBar = styled.div`
  display: flex; align-items: stretch;
  gap: 10px;
  margin: 12px 0 24px;
  padding: 10px 14px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  flex-wrap: wrap;
`

const TripleScoreBarLabel = styled.span`
  align-self: center;
  font-size: ${font.caption};
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 800;
  padding-right: 8px;
  border-right: 1px solid #ececec;
  margin-right: 4px;
`

const TripleScorePill = styled.div`
  display: inline-flex; align-items: center; gap: 10px;
  padding: 6px 14px;
  background: ${({ $tint, $muted }) => $muted ? surface.wash : `${$tint}10`};
  border: 1px solid ${({ $tint, $muted }) => $muted ? line.strong : `${$tint}40`};
  border-radius: 999px;
  color: ${({ $tint, $muted, theme }) => $muted ? theme.colors.grey[800] : $tint};
  opacity: ${({ $muted }) => $muted ? 0.7 : 1};
`

const PillKind = styled.span`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

const PillValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 22px;
  letter-spacing: -0.5px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
`

const PillHint = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 500;
`

const TripleScoreCard = styled.div`
  position: relative;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${line.base};
  border-left: 4px solid ${({ $tint, $muted }) => $muted ? line.strong : $tint};
  border-radius: 12px;
  padding: 14px 18px 14px;
  display: flex; flex-direction: column; gap: 8px;
  transition: transform 200ms ease, box-shadow 200ms ease;
  opacity: ${({ $muted }) => $muted ? 0.7 : 1};
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${shadow.lift};
  }
`

const TripleScoreHeader = styled.div`
  display: flex; flex-direction: column; gap: 2px;
`

const TripleScoreLabel = styled.div`
  font-size: 11px;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.haiti};
  font-weight: 800;
`

const TripleScoreSubLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const TripleScoreValue = styled.div`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${({ $muted }) => $muted ? font.emphasis : font.display};
  letter-spacing: -0.8px;
  color: ${({ $muted, theme }) => $muted ? theme.colors.grey[800] : theme.colors.haiti};
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  font-weight: ${({ $muted }) => $muted ? 500 : 400};
`

const WizardHero = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  gap: 20px;
  padding: 20px 22px 20px 26px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.haiti};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  margin-bottom: 16px;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    top: 0; bottom: 0; left: 0;
    width: 4px;
    background: #33becc;
  }
  @media (max-width: ${bp.mobile}) {
    grid-template-columns: minmax(0, 1fr);
    gap: 14px;
    padding: 16px 14px 16px 18px;
  }
`

const WizardHeroLeft = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
  display: flex; flex-direction: column; gap: 6px;
`

const WizardEyebrow = styled.span`
  font-size: ${font.caption};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 800;
`

const WizardTitle = styled.h2`
  margin: 0;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 22px;
  letter-spacing: -0.5px;
  line-height: 1.2;
`

const WizardSubtitle = styled.p`
  margin: 0 0 4px;
  font-size: ${font.body};
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[800]};
  max-width: 44ch;
`

const WizardCta = styled.button`
  font: inherit;
  align-self: flex-start;
  margin-top: 8px;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 18px;
  background: #33becc;
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: ${font.body};
  font-weight: 800;
  letter-spacing: 0.04em;
  cursor: pointer;
  min-height: 44px;
  transition: background 150ms ease;
  &:hover { background: #2aa6b3; }
  &:focus-visible { outline: 2px solid #1d7f89; outline-offset: 2px; }
  @media (max-width: ${bp.mobile}) {
    align-self: stretch;
    justify-content: center;
  }
`

const WizardHeroRight = styled.div`
  position: relative; z-index: 1; min-width: 0;
  align-self: center;
`

const WizardPreview = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  padding: 12px 14px;
  background: ${surface.wash};
  border: 1px solid ${line.base};
  border-radius: 12px;
`

const WizardPreviewRow = styled.div`
  display: flex; align-items: center; gap: 10px;
`

const WizardPreviewSeverity = styled.span`
  width: 6px; height: 16px;
  background: ${({ $tint }) => $tint};
  border-radius: ${radius.xs};
  flex-shrink: 0;
`

const WizardPreviewName = styled.span`
  flex: 1; min-width: 0;
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.haiti};
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`

const WizardPreviewCount = styled.span`
  font-size: ${font.caption};
  letter-spacing: 0.04em;
  padding: 2px 8px;
  background: ${surface.wash};
  color: ${({ theme }) => theme.colors.grey[800]};
  border: 1px solid ${line.base};
  border-radius: 999px;
  font-weight: 700;
`

const WizardPreviewMore = styled.div`
  padding: 4px 0 0 16px;
  font-size: ${font.small};
  color: ${({ theme }) => theme.colors.grey[800]};
  font-style: italic;
`

const EmptyTabState = styled.div`
  min-height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 24px 18px;
  text-align: center;
  background: ${({ theme }) => theme.colors.white};
  border: 1px dashed ${line.base};
  border-radius: ${radius.md};
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: 13px;
  line-height: 1.5;
`

const WidgetScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  max-height: 264px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  margin-right: -14px;
  padding-right: 14px;
  scrollbar-width: thin;
  scrollbar-color: ${line.strong} transparent;
  &::-webkit-scrollbar { width: 7px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: ${line.strong}; border-radius: 999px; }
  &::-webkit-scrollbar-thumb:hover { background: ${ink.mute}; }
`

const WidgetStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: auto;
  min-height: 0;
`

const WidgetMetaBar = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const WidgetCount = styled.span`
  font-size: ${font.caption};
  font-weight: 600;
  color: ${ink.mute};
  cursor: ${({ title }) => (title ? 'help' : 'default')};
`

const WidgetLink = styled.button`
  font: inherit;
  border: none;
  background: none;
  padding: 0;
  font-size: ${font.caption};
  font-weight: 700;
  color: #2aa6b3;
  cursor: pointer;

  &:hover { text-decoration: underline; }
`

const StatRow = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  & > * {
    flex: 1 1 90px;
  }
`

const STAT_TONE = {
  bad: { fg: '#b00020', bg: '#fdecea' },
  warn: { fg: '#d97706', bg: '#fef3c7' },
  neutral: { fg: '#33becc', bg: 'rgba(51, 190, 204, 0.10)' },
  muted: { fg: '#64748b', bg: '#f1f5f9' },
}

const StatPill = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${({ $tone }) => (STAT_TONE[$tone] || STAT_TONE.neutral).bg};
  border: 1px solid ${({ $tone }) => `${(STAT_TONE[$tone] || STAT_TONE.neutral).fg}26`};
`

const StatNum = styled.span`
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: ${ink.base};
`

const StatLbl = styled.span`
  font-size: ${font.caption};
  font-weight: 600;
  color: ${ink.mute};
`

const RiskRow = styled.button`
  text-align: left;
  width: 100%;
  font: inherit;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${line.base};
  background: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  transition: ${transition};

  &:hover {
    border-color: ${ink.mute};
    background: rgba(12, 18, 41, 0.02);
  }
`

const RiskScore = styled.span`
  flex-shrink: 0;
  min-width: 48px;
  text-align: center;
  padding: 4px 8px;
  border-radius: ${radius.pill};
  font-size: 13px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${({ $tint }) => $tint};
  background: ${({ $tint }) => `${$tint}14`};
  border: 1px solid ${({ $tint }) => `${$tint}40`};
`

const RiskMain = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const RiskName = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${ink.base};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RiskReasons = styled.span`
  font-size: ${font.caption};
  color: ${ink.mute};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RiskArrow = styled.span`
  flex-shrink: 0;
  color: ${ink.mute};
  font-weight: 700;
`

const ChipWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const VetChip = styled.span`
  padding: 3px 10px;
  border-radius: ${radius.pill};
  font-size: ${font.caption};
  font-weight: 700;
  white-space: nowrap;
  color: ${({ $fg }) => $fg};
  background: ${({ $bg }) => $bg};
  border: 1px solid ${({ $fg }) => `${$fg}33`};
`

const ContractorHead = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`

const WidgetHead = ({ icon: Icon, value, label, sub }) => (
  <HeadStat>
    <HeadIcon aria-hidden='true'>
      <Icon style={{ fontSize: 18 }} />
    </HeadIcon>
    <HeadText>
      <HeadTitle>
        <HeadNum>{value}</HeadNum>
        <HeadWord>{label}</HeadWord>
      </HeadTitle>
      {sub ? <HeadSub>{sub}</HeadSub> : null}
    </HeadText>
  </HeadStat>
)

const HeadStat = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`

const HeadIcon = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: ${radius.sm};
  background: #f1f5f9;
  color: #475569;
`

const HeadText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const HeadTitle = styled.span`
  display: flex;
  align-items: baseline;
  gap: 6px;
`

const HeadNum = styled.span`
  font-size: 22px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: ${ink.base};
`

const HeadWord = styled.span`
  font-size: ${font.emphasis};
  font-weight: 600;
  color: ${ink.base};
`

const HeadSub = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: ${font.small};
  font-weight: 600;
  color: ${ink.mute};
`

const ActiveDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $on }) => ($on ? '#15803d' : '#cbd2dc')};
`

const DocAvatar = styled.span`
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: ${radius.round};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  color: ${({ $tint }) => $tint};
  background: ${({ $tint }) => `${$tint}1A`};
  border: 1px solid ${({ $tint }) => `${$tint}40`};
`

const AllClear = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #15803d33;
  background: #dcfce7;
  color: #15803d;
  font-size: 13px;
  font-weight: 600;

  & > button { margin-left: auto; color: #15803d; }
`

const AllClearDot = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #15803d;
`

const EmptyIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  margin-bottom: 4px;
  border-radius: ${radius.round};
  color: ${ink.mute};
  background: ${surface.wash};
`

const EmptyTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${ink.base};
`

const EmptyText = styled.span`
  max-width: 320px;
  font-size: 12px;
  line-height: 1.5;
  color: ${ink.mute};
`

const PermitRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${line.base};
  background: ${({ theme }) => theme.colors.white};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: ${transition};

  &:hover {
    border-color: ${({ $clickable }) => ($clickable ? ink.mute : line.base)};
  }
`

const PermitMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const PermitTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: ${ink.base};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PermitMeta = styled.span`
  font-size: ${font.caption};
  color: ${ink.mute};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PermitStatusTag = styled.span`
  flex-shrink: 0;
  padding: 3px 10px;
  border-radius: ${radius.pill};
  font-size: ${font.caption};
  font-weight: 700;
  color: ${ink.mute};
  background: rgba(12, 18, 41, 0.05);
  border: 1px solid ${line.base};
  text-transform: capitalize;
`

const HighRiskDot = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #b00020;
`

const DocRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${line.base};
  background: ${({ theme }) => theme.colors.white};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: ${transition};

  &:hover {
    border-color: ${({ $clickable }) => ($clickable ? ink.mute : line.base)};
  }
`

const DocIcon = styled.span`
  flex-shrink: 0;
  display: inline-flex;
`

const DocMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const DocName = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${ink.base};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const DocMeta = styled.span`
  font-size: ${font.caption};
  color: ${ink.mute};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ExpiryTag = styled.span`
  flex-shrink: 0;
  padding: 3px 9px;
  border-radius: ${radius.pill};
  font-size: ${font.caption};
  font-weight: 700;
  color: ${({ $expired }) => ($expired ? '#b00020' : '#d97706')};
  background: ${({ $expired }) => ($expired ? '#fdecea' : '#fef3c7')};
  border: 1px solid ${({ $expired }) => ($expired ? '#b0002033' : '#d9770633')};
`

const SectionGap = styled.div` height: 4px; `

// Slim horizontal divider matching SAP Fiori section bars.
// One line of content total: eyebrow + title + subtle subtitle on the right.
// No box, no card — just rhythm between content groups.
const DomainGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${line.base}; border-radius: 12px; padding: 18px 20px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`

const PortfolioSummaryCard = styled.div`
  display: flex; flex-direction: column; gap: 8px;
  padding: 16px; border-radius: ${radius.md};
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px solid ${line.base};
`
const SummaryLabel = styled.div`
  font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]}; font-weight: 700; margin-bottom: 6px;
`
const SummaryRow = styled.div`
  display: flex; align-items: center; gap: 10px; font-size: 13px; color: ${({ theme }) => theme.colors.haiti};
`
const Dot = styled.span`
  width: 8px; height: 8px; border-radius: 50%;
  background: ${({ $colour }) => $colour};
`

const BpComplianceGrid = styled.div`
  display: grid; gap: 12px;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
`

// Sort key for the at-risk-first ordering of the per-BP cards.
// Lower key = render first. Sequence:
//   1. Most overdue tasks (huge negative key)
//   2. Lowest score (linear)
//   3. Fewest satisfied
const riskSortKey = entry => {
  const overdue = entry.rollup?.overdue || 0
  const score   = entry.rollup?.score
  const total   = entry.rollup?.totalTasks || 0
  const onTime  = entry.rollup?.completedOnTime || 0
  // overdue weighs the most (every overdue subtracts 100); then score
  // (0–100 where lower is worse); then satisfied count for tie-breaks.
  return -overdue * 100 + (score ?? 100) + (total > 0 ? (onTime / total) : 1)
}

const BpRiskCard = styled.div`
  display: flex; flex-direction: column; gap: 10px;
  padding: 14px 16px 12px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${line.base};
  border-left: 4px solid ${({ $tint }) => $tint};
  border-radius: 12px;
  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${shadow.lift};
    border-color: ${({ $tint }) => $tint};
    border-left-width: 5px;
  }
`

const BpRiskTop = styled.div`
  display: flex; align-items: center; gap: 14px;
  min-width: 0;
`

const BpRiskScore = styled.div`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.display};
  line-height: 1;
  letter-spacing: -1.2px;
  color: ${({ $tint }) => $tint};
  font-variant-numeric: tabular-nums;
  width: 80px;
  flex-shrink: 0;
`

const BpRiskName = styled.div`
  display: flex; flex-direction: column; gap: 3px;
  min-width: 0;
`

const BpRiskTitle = styled.div`
  font-weight: 700; font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const BpRiskBand = styled.span`
  display: inline-flex; align-self: flex-start;
  font-size: 10px; font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ $tint }) => `${$tint}1A`};
  color: ${({ $tint }) => $tint};
  border: 1px solid ${({ $tint }) => `${$tint}33`};
`

const BpRiskBar = styled.div`
  display: flex; height: 8px; border-radius: 999px;
  background: ${surface.wash};
  overflow: hidden;
`

const BpRiskSeg = styled.span`
  height: 100%;
  &:first-child { border-radius: 999px 0 0 999px; }
  &:last-child  { border-radius: 0 999px 999px 0; }
  &:only-child  { border-radius: 999px; }
  transition: width 320ms ease;
`

const BpRiskFooter = styled.div`
  display: flex; align-items: center;
`

const BpRiskHeadline = styled.div`
  font-size: 12px;
  color: ${({ $urgent, $ok, theme }) =>
    $urgent ? '#b00020' : $ok ? '#1f8c97' : theme.colors.grey[800]};
  line-height: 1.4;
  strong { font-weight: 800; }
  em { font-style: normal; font-weight: 700; }
`

const BpCard = styled.div`
  display: grid; grid-template-columns: auto 1fr; gap: 14px;
  align-items: center;
  padding: 16px 18px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${line.base}; border-radius: 12px;
  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${shadow.lift};
    border-color: ${({ theme }) => theme.colors.secondary};
  }
`
const BpCardBody = styled.div` min-width: 0; display: flex; flex-direction: column; gap: 6px; `
const BpCardHead = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 10px;
`
const BpCardName = styled.div`
  font-weight: 600; font-size: 15px; color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`
const BpCardMeta = styled.div`
  font-size: 12px; color: ${({ theme }) => theme.colors.grey[800]};
`
const Overdue = styled.span` color: #b00020; font-weight: 600; `
const ChipRow = styled.div` display: flex; flex-wrap: wrap; gap: 5px; `
const Chip = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 8px; border-radius: 999px;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px solid ${line.base}; font-size: 11px; color: ${({ theme }) => theme.colors.haiti};
`
const ChipDot = styled.span` width: 6px; height: 6px; border-radius: 50%; `

const Card = styled.div`
  padding: 18px 20px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${line.base}; border-radius: 12px;
`

const BpsList = styled.div` display: flex; flex-wrap: wrap; `

const UpcomingEventsScroll = styled.div`
  display: flex; flex-wrap: nowrap; gap: 12px;
  overflow-x: auto; padding-bottom: 8px;
  &::-webkit-scrollbar { height: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: ${line.strong}; border-radius: ${radius.pill}; }
`
const EventCard = styled.div`
  display: flex; gap: 12px; padding: 16px 18px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  min-width: 300px;
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
  &:hover {
    transform: translateY(-2px);
    border-color: #d6d6da;
    box-shadow: ${shadow.lift};
  }
`
const EventThumbnail = styled.div`
  width: 56px; height: 72px; border-radius: ${radius.sm};
  background: ${surface.linen};
  border: 1px solid ${line.base};
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; flex-shrink: 0; overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
`
const EventContent = styled.div` display: flex; flex-direction: column; gap: 4px; min-width: 0; `
const EventFileName = styled.div`
  font-weight: 600; font-size: ${font.emphasis}; color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 220px;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`
const EventDate = styled.div` font-size: ${font.small}; color: ${ink.mute}; `
const EventUrgency = styled.span`
  color: ${({ $urgent, theme }) => ($urgent ? '#b00020' : theme.colors.grey[800])};
  font-weight: ${({ $urgent }) => ($urgent ? 600 : 400)};
`
const EventBuilding = styled.div`
  font-size: 12px; color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer; margin-top: 2px;
  &:hover { text-decoration: underline; }
`

const DetailsWrapper = styled.div`
  width: 280px; position: fixed;
  top: ${({ theme }) => theme.dimensions.topBarHeight};
  background-color: ${({ theme }) => theme.colors.white};
  right: 0; bottom: 0;
  padding: 25px 20px; overflow-y: auto;
  box-shadow: -4px 0 15px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  @media (max-width: 1024px) { position: static; width: 100%; box-shadow: none; }
`

const UnitWelcomeCard = styled.div`
  background: #ffffff; padding: 40px; margin: 60px auto;
  border-radius: 12px; text-align: center; max-width: 700px;
  border: 1px solid ${line.base};
`
const UnitAccessButton = styled.button`
  outline: 0; background-color: ${ink.base}; color: #fff;
  border: none; box-shadow: ${shadow.card};
  border-radius: ${radius.pill}; min-width: 200px; height: 40px;
  transition: ${transition}; cursor: pointer; font-size: ${font.emphasis}; font-weight: 700; padding: 2px 18px;
  &:hover { background-color: ${({ theme }) => theme.colors.secondary}; box-shadow: ${shadow.lift}; }
`

const WidgetNote = styled.p`
  margin: 10px 0 0;
  font-size: 14px;
  line-height: 1.45;
  color: #475467;
`
